/* ==========================================================================
   PREWORN BROWSE ENGINE
   PREWORN MySQL catalogue search and collection browsing
   - 50 products per request
   - exact totals beyond Shopify's 1,000 / 25,000 result limits
   - metafield-backed catalogue filters
   - Size buttons / Colour swatches
   - numeric pagination across the complete catalogue
   - Shopify Storefront API and native results remain as safety fallbacks
   ========================================================================== */

(function () {
  'use strict';

  var CONFIG = {
    catalogApiEnabled: true,
    catalogApiUrl: 'https://preworn.co.uk/sandbox/shopify-catalog-index/shopify_catalog_api.php',
    catalogRequestTimeout: 12000,
    catalogFailureCooldown: 15000,
    shopDomain: 'preworn-clothing.myshopify.com',
    storefrontToken: '601dc93f3b5bff39a996e00c51b4276c',
    apiVersion: '2026-07',
    pageSize: 50,
    newProductWindowDays: 30,
    shopifyFallbackMaxResults: 25000,
    maxQuickFilters: 12,
    wishlistStorageKey: 'preworn:wishlist'
  };

  var state = {
    mode: 'search',
    query: '',
    trending: false,
    brandQuery: '',
    tagQuery: '',
    tagQueries: [],
    tagLabel: '',
    similarSku: '',
    similarTitle: '',
    collectionHandle: '',
    collectionTitle: '',
    collectionCount: null,
    currentPage: 1,
    pageCursors: { 1: null },
    selectedValues: {},
    priceMin: '',
    priceMax: '',
    sortKey: 'RELEVANCE',
    reverse: false,
    quickFilterOrder: [],
    collectionQueryAttempted: false,
    collectionQueryMatched: false,
    collectionQueryFilterId: '',
    collectionKeywordSearch: false,
    collectionSearchQuery: '',
    collectionScopeFilters: [],
    catalogFilters: [],
    catalogFacetsDeferred: false,
    catalogFacetsLoading: false,
    catalogFacetRetryCount: 0,
    catalogFacetRetryTimer: null,
    catalogUnavailableUntil: 0,
    collectionCatalogStale: false,
    openFilterGroups: {},
    expandedFilterGroups: {},
    usingCatalog: false,
    loading: false,
    pendingReload: false,
    lastResult: null
  };

  var app = null;

  var SEARCH_QUERY = [
    'query PrewornSearch(',
    '  $query: String!,',
    '  $first: Int!,',
    '  $after: String,',
    '  $filters: [ProductFilter!],',
    '  $sortKey: SearchSortKeys,',
    '  $reverse: Boolean',
    ') {',
    '  search(',
    '    query: $query',
    '    first: $first',
    '    after: $after',
    '    types: [PRODUCT]',
    '    prefix: LAST',
    '    unavailableProducts: HIDE',
    '    productFilters: $filters',
    '    sortKey: $sortKey',
    '    reverse: $reverse',
    '  ) {',
    '    totalCount',
    '    pageInfo {',
    '      hasNextPage',
    '      hasPreviousPage',
    '      startCursor',
    '      endCursor',
    '    }',
    '    productFilters {',
    '      id',
    '      label',
    '      type',
    '      presentation',
    '      values {',
    '        id',
    '        label',
    '        count',
    '        input',
    '        swatch {',
    '          color',
    '          image {',
    '            image {',
    '              url',
    '            }',
    '          }',
    '        }',
    '      }',
    '    }',
    '    nodes {',
    '      ... on Product {',
    '        id',
    '        handle',
    '        title',
    '        vendor',
    '        productType',
    '        createdAt',
    '        availableForSale',
    '        onlineStoreUrl',
    '        featuredImage {',
    '          url',
    '          altText',
    '          width',
    '          height',
    '        }',
    '        priceRange {',
    '          minVariantPrice {',
    '            amount',
    '            currencyCode',
    '          }',
    '        }',
    '        compareAtPriceRange {',
    '          minVariantPrice {',
    '            amount',
    '            currencyCode',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  var COLLECTION_QUERY = [
    'query PrewornCollection(',
    '  $handle: String!,',
    '  $first: Int!,',
    '  $after: String,',
    '  $filters: [ProductFilter!],',
    '  $sortKey: ProductCollectionSortKeys,',
    '  $reverse: Boolean',
    ') {',
    '  collection(handle: $handle) {',
    '    id',
    '    title',
    '    handle',
    '    products(',
    '      first: $first',
    '      after: $after',
    '      filters: $filters',
    '      sortKey: $sortKey',
    '      reverse: $reverse',
    '    ) {',
    '      pageInfo {',
    '        hasNextPage',
    '        hasPreviousPage',
    '        startCursor',
    '        endCursor',
    '      }',
    '      filters {',
    '        id',
    '        label',
    '        type',
    '        presentation',
    '        values {',
    '          id',
    '          label',
    '          count',
    '          input',
    '          swatch {',
    '            color',
    '            image {',
    '              image {',
    '                url',
    '              }',
    '            }',
    '          }',
    '        }',
    '      }',
    '      nodes {',
    '        id',
    '        handle',
    '        title',
    '        vendor',
    '        productType',
    '        createdAt',
    '        availableForSale',
    '        onlineStoreUrl',
    '        featuredImage {',
    '          url',
    '          altText',
    '          width',
    '          height',
    '        }',
    '        priceRange {',
    '          minVariantPrice {',
    '            amount',
    '            currencyCode',
    '          }',
    '        }',
    '        compareAtPriceRange {',
    '          minVariantPrice {',
    '            amount',
    '            currencyCode',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function boot() {
    app = document.getElementById('PrewornWideSearch');

    if (!app) {
      return;
    }

    var configuredCatalogApi = app.getAttribute('data-catalog-api') ||
      CONFIG.catalogApiUrl;

    if (!configuredCatalogApi && (!CONFIG.storefrontToken ||
        CONFIG.storefrontToken === 'PASTE_PUBLIC_STOREFRONT_TOKEN_HERE')) {
      renderFatal(
        'No catalogue data source has been configured.'
      );
      showNativeFallback();
      return;
    }

    state.mode = app.getAttribute('data-mode') || 'search';
    state.collectionHandle = app.getAttribute('data-collection-handle') || '';
    state.collectionTitle = app.getAttribute('data-collection-title') || '';

    var params = new URLSearchParams(window.location.search);
    var requestedSort = (params.get('sort') || '').toLowerCase();
    state.trending = requestedSort === 'trending';
    state.brandQuery = (params.get('brand') || '').trim();
    state.tagQueries = params.getAll('tags[]').map(function (value) {
      return String(value || '').trim();
    }).filter(function (value, index, values) {
      if (!value) return false;
      var key = value.toLowerCase();
      return values.findIndex(function (candidate) {
        return candidate.toLowerCase() === key;
      }) === index;
    });
    state.tagQuery = (params.get('tag') || '').trim();
    if (!state.tagQueries.length && state.tagQuery) state.tagQueries = [state.tagQuery];
    if (!state.tagQuery && state.tagQueries.length) state.tagQuery = state.tagQueries[0];
    state.tagLabel = (params.get('tag_label') || '').trim();
    if (!state.tagLabel && state.tagQuery) {
      state.tagLabel = state.tagQueries.map(function (tag) {
        return tag.replace(/^[^_]+_/, '').replace(/_/g, ' ').trim();
      }).join(' ');
    }
    function addInitialCatalogSelection(parameter, value) {
      value = String(value || '').trim();
      if (!value) return;
      state.selectedValues['catalog:' + parameter + ':' + value.toLowerCase()] = {
        input: {
          catalogParameter: parameter,
          catalogValue: value
        },
        label: value
      };
    }
    (params.get('type') || '').split(',').forEach(function (value) {
      addInitialCatalogSelection('type', value);
    });
    var initialDepartments = (params.get('department') || '').split(',').map(function (value) {
      return value.trim();
    }).filter(function (value) {
      return value !== '';
    });
    if (initialDepartments.length === 1 && /^kids?$/i.test(initialDepartments[0])) {
      initialDepartments = ['Girls', 'Boys'];
    } else if (initialDepartments.length === 1 && /^womens?$/i.test(initialDepartments[0])) {
      initialDepartments = ['Women', 'Womens'];
    } else if (initialDepartments.length === 1 && /^mens?$/i.test(initialDepartments[0])) {
      initialDepartments = ['Men', 'Mens'];
    }
    initialDepartments.forEach(function (department) {
      addInitialCatalogSelection('department', department);
    });
    [
      'category', 'style', 'vendor', 'size', 'colour',
      'condition', 'material', 'pattern'
    ].forEach(function (parameter) {
      (params.get(parameter) || '').split(',').forEach(function (value) {
        addInitialCatalogSelection(parameter, value);
      });
    });
    state.priceMin = (params.get('price_min') || '').trim();
    state.priceMax = (params.get('price_max') || '').trim();
    state.similarSku = (params.get('similar_sku') || '').trim();
    state.similarTitle = (params.get('similar_title') || '').trim();
    state.query = (
      app.getAttribute('data-query') ||
      params.get('q') ||
      state.brandQuery ||
      state.tagLabel ||
      ''
    ).trim();

    var rawCollectionCount = app.getAttribute('data-collection-count');
    if (rawCollectionCount !== null && rawCollectionCount !== '') {
      state.collectionCount = Number(rawCollectionCount);
    }

    if (state.mode === 'collection') {
      state.sortKey = 'COLLECTION_DEFAULT';

      if (requestedSort === 'price_asc') {
        state.sortKey = 'PRICE';
        state.reverse = false;
      } else if (requestedSort === 'price_desc') {
        state.sortKey = 'PRICE';
        state.reverse = true;
      } else if (requestedSort === 'newest') {
        state.sortKey = 'CREATED';
        state.reverse = true;
      }

      if (!state.collectionHandle) {
        renderFatal('No collection handle supplied.');
        showNativeFallback();
        return;
      }
    } else {
      state.mode = 'search';

      if (state.trending) state.sortKey = 'TRENDING';
      else if (requestedSort === 'price_asc') {
        state.sortKey = 'PRICE';
        state.reverse = false;
      } else if (requestedSort === 'price_desc') {
        state.sortKey = 'PRICE';
        state.reverse = true;
      } else if (requestedSort === 'newest') {
        state.sortKey = 'CREATED';
        state.reverse = true;
      }

      if (!state.query && !state.similarSku && !state.trending &&
          Object.keys(state.selectedValues).length === 0) {
        renderFatal('No search term supplied.');
        showNativeFallback();
        return;
      }
    }

    injectStyles();
    buildShell();
    bindStaticEvents();
    syncSortControl();
    bindBrowserHistory();
    var initialPage = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);
    loadPage(initialPage, null);
  }

  function headingFilterGroups() {
    var definitions = {
      style: 'Style',
      type: 'Type',
      pattern: 'Pattern',
      vendor: 'Brand',
      category: 'Category',
      material: 'Material',
      size: 'Size',
      condition: 'Condition',
      colour: 'Colour',
      department: 'Department'
    };
    var priority = [
      'style', 'type', 'pattern', 'vendor', 'category',
      'material', 'size', 'condition', 'colour', 'department'
    ];
    var tagScope = structuredFacetScopeFromTags(state.tagQueries) || {};
    var groups = {};
    Object.keys(state.selectedValues).forEach(function (key) {
      var selected = state.selectedValues[key] || {};
      var input = selected.input || {};
      var parameter = String(input.catalogParameter || '').toLowerCase();
      if (!parameter) {
        var keyMatch = String(key).match(/^catalog:([^:]+):/i);
        parameter = keyMatch ? keyMatch[1].toLowerCase() : '';
      }
      if (!definitions[parameter]) return;
      var value = String(selected.label || input.catalogValue || '').trim();
      if (!value) return;

      var scopedValues = String(tagScope[parameter] || '').split(',').map(function (item) {
        return item.trim().toLowerCase();
      });
      if (scopedValues.indexOf(value.toLowerCase()) !== -1) return;
      if (parameter === 'vendor' && state.brandQuery &&
          value.toLowerCase() === state.brandQuery.toLowerCase()) return;

      if (parameter === 'department') {
        if (/^womens?$/i.test(value)) value = 'Women';
        else if (/^mens?$/i.test(value)) value = 'Men';
      }
      groups[parameter] = groups[parameter] || [];
      if (groups[parameter].map(function (item) {
        return item.toLowerCase();
      }).indexOf(value.toLowerCase()) === -1) {
        groups[parameter].push(value);
      }
    });
    return priority.filter(function (parameter) {
      return groups[parameter] && groups[parameter].length;
    }).map(function (parameter) {
      return {
        label: definitions[parameter],
        values: groups[parameter]
      };
    });
  }

  function headingFilterContextHtml() {
    var groups = headingFilterGroups();
    if (!groups.length) return '';
    return '<span class="pw-search__query-context">' + groups.map(function (group) {
      return '<span><b>' + escapeHtml(group.label) + ':</b> ' +
        escapeHtml(group.values.join(' / ')) + '</span>';
    }).join('<i aria-hidden="true">·</i>') + '</span>';
  }

  function searchHeadingHtml() {
    var heading;
    if (state.mode === 'collection') {
      heading = escapeHtml(state.collectionTitle);
    } else if (state.trending) {
      heading = state.tagQuery
        ? 'Shop <strong>' + escapeHtml(state.tagLabel) + ' trending</strong>'
        : 'Shop <strong>what’s trending</strong>';
    } else if (state.tagQuery) {
      heading = 'Shop <strong>' + escapeHtml(state.tagLabel) + '</strong>';
    } else if (state.similarSku) {
      heading = '<span class="pw-search__similar-kicker">SIMILAR TO THE ONE JUST BOUGHT</span>' +
        '<strong>' + escapeHtml(state.similarTitle || state.query || 'One-off find') + '</strong>';
    } else {
      heading = 'Search results for <strong>"' + escapeHtml(state.query) + '"</strong>';
    }
    return heading + headingFilterContextHtml();
  }

  function renderSearchHeading() {
    var heading = document.querySelector('.pw-search__query');
    if (heading) heading.innerHTML = searchHeadingHtml();
  }

  function buildShell() {
    var heading = searchHeadingHtml();

    var defaultSortLabel = state.trending
      ? 'Trending now'
      : (state.similarSku
        ? 'Best match'
        : (state.mode === 'collection' ? 'Featured' : 'Relevance'));
    var newestSortOption = state.mode === 'collection'
      ? '<option value="DATE_DESC">Newly listed</option>'
      : '';

    app.innerHTML =
      '<div class="pw-search">' +
        '<aside class="pw-search__filters" id="PrewornFilterRail">' +
          '<div class="pw-search__filter-head">' +
            '<strong>Filter</strong>' +
            '<div class="pw-search__filter-actions">' +
              '<button type="button" class="pw-search__clear" data-pw-clear>Clear all</button>' +
              '<button type="button" class="pw-search__filter-close" data-pw-filter-close aria-label="Close filters">' +
                '<span aria-hidden="true">&times;</span>' +
              '</button>' +
            '</div>' +
          '</div>' +
          '<div id="PrewornSearchFiltersBody"></div>' +
        '</aside>' +

        '<section class="pw-search__results">' +
          '<header class="pw-search__intro">' +
            '<h1 class="pw-search__query">' + heading + '</h1>' +
            '<p class="pw-search__count" id="PrewornSearchCount">Loading products…</p>' +
          '</header>' +

          '<div class="pw-quick-filters" id="PrewornQuickFilters" aria-label="Quick filters"></div>' +
          '<div class="pw-active-filters" id="PrewornActiveFilters" hidden></div>' +

          '<div class="pw-search__toolbar">' +
            '<div class="pw-search__toolbar-summary">' +
              '<strong id="PrewornToolbarCount">Loading…</strong>' +
              '<button type="button" class="pw-search__filter-toggle" data-pw-filter-toggle aria-expanded="false" aria-controls="PrewornFilterRail">' +
                'Filter <span id="PrewornActiveFilterCount">0</span>' +
              '</button>' +
            '</div>' +

            '<label class="pw-search__sort">' +
              '<span>Sort:</span>' +
              '<select id="PrewornSearchSort">' +
                '<option value="DEFAULT">' + defaultSortLabel + '</option>' +
                newestSortOption +
                '<option value="PRICE_ASC">Price: low to high</option>' +
                '<option value="PRICE_DESC">Price: high to low</option>' +
              '</select>' +
            '</label>' +
          '</div>' +

          '<div class="pw-search__loading" id="PrewornSearchLoading" hidden>' +
            '<span class="pw-search__spinner"></span> Loading products…' +
          '</div>' +

          '<div class="pw-search__error" id="PrewornSearchError" hidden></div>' +

          '<ul class="pw-search__grid" id="PrewornSearchGrid"></ul>' +

          '<nav class="pw-search__pagination" id="PrewornSearchPagination" aria-label="Product pagination"></nav>' +
        '</section>' +
      '</div>';

  }

  function bindStaticEvents() {
    var clear = app.querySelector('[data-pw-clear]');
    if (clear) {
      clear.addEventListener('click', function () {
        state.selectedValues = {};
        state.priceMin = '';
        state.priceMax = '';
        clearCollectionQuery();
        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var sort = document.getElementById('PrewornSearchSort');
    if (sort) {
      sort.addEventListener('change', function () {
        state.trending = false;
        if (this.value === 'PRICE_ASC') {
          state.sortKey = 'PRICE';
          state.reverse = false;
        } else if (this.value === 'PRICE_DESC') {
          state.sortKey = 'PRICE';
          state.reverse = true;
        } else if (this.value === 'DATE_DESC') {
          state.sortKey = 'CREATED';
          state.reverse = true;
        } else {
          state.sortKey = state.mode === 'collection'
            ? 'COLLECTION_DEFAULT'
            : 'RELEVANCE';
          state.reverse = false;
        }

        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var filterToggle = app.querySelector('[data-pw-filter-toggle]');
    var filterRail = document.getElementById('PrewornFilterRail');

    if (filterToggle && filterRail) {
      filterToggle.addEventListener('click', function () {
        setMobileFiltersOpen(!filterRail.classList.contains('is-mobile-open'));
      });
    }

    var filterClose = app.querySelector('[data-pw-filter-close]');
    if (filterClose) {
      filterClose.addEventListener('click', function () {
        setMobileFiltersOpen(false, true);
      });
    }

    app.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && filterRail &&
          filterRail.classList.contains('is-mobile-open')) {
        setMobileFiltersOpen(false, true);
      }
    });

    window.addEventListener('resize', function () {
      if (!filterRail) return;
      var keepOpen = window.innerWidth <= 749 &&
        filterRail.classList.contains('is-mobile-open');
      setMobileFiltersOpen(keepOpen, false);
    });

    setMobileFiltersOpen(false, false);
  }

  function setMobileFiltersOpen(open, restoreFocus) {
    var filterRail = document.getElementById('PrewornFilterRail');
    var filterToggle = app && app.querySelector('[data-pw-filter-toggle]');
    if (!filterRail) return;

    filterRail.classList.toggle('is-mobile-open', !!open);
    if (!open && window.innerWidth > 749) {
      filterRail.removeAttribute('aria-hidden');
    } else {
      filterRail.setAttribute('aria-hidden', open ? 'false' : 'true');
    }
    document.body.classList.toggle('pw-search-filter-open', !!open);

    if (filterToggle) {
      filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    if (open) {
      var closeButton = filterRail.querySelector('[data-pw-filter-close]');
      if (closeButton) {
        window.setTimeout(function () { closeButton.focus(); }, 0);
      }
    } else if (restoreFocus && filterToggle) {
      filterToggle.focus();
    }
  }

  function resetPagination() {
    state.currentPage = 1;
    state.pageCursors = { 1: null };
  }

  function syncSortControl() {
    var sort = document.getElementById('PrewornSearchSort');
    if (!sort) return;
    if (state.sortKey === 'PRICE') {
      sort.value = state.reverse ? 'PRICE_DESC' : 'PRICE_ASC';
    } else if (state.sortKey === 'CREATED') {
      sort.value = 'DATE_DESC';
    } else {
      sort.value = 'DEFAULT';
    }
  }

  function historyUrl(pageNumber) {
    var url = new URL(window.location.href);
    [
      'department', 'category', 'type', 'style', 'vendor', 'size',
      'colour', 'condition', 'material', 'pattern', 'price_min',
      'price_max', 'page'
    ].forEach(function (parameter) {
      url.searchParams.delete(parameter);
    });
    if (state.mode === 'collection' && !state.query) {
      url.searchParams.delete('q');
    }
    appendCatalogFilters(url.searchParams);
    url.searchParams.set('sort', catalogSortValue());
    if (pageNumber > 1) url.searchParams.set('page', String(pageNumber));
    return url.pathname + url.search + url.hash;
  }

  function navigateWithHistory(pageNumber, afterCursor) {
    var target = historyUrl(pageNumber);
    var current = window.location.pathname + window.location.search + window.location.hash;
    if (target !== current) {
      window.history.pushState(
        { prewornBrowse: true, page: pageNumber },
        '',
        target
      );
    }
    renderSearchHeading();
    loadPage(pageNumber, afterCursor);
  }

  function bindBrowserHistory() {
    if (state.browserHistoryBound) return;
    state.browserHistoryBound = true;
    window.addEventListener('popstate', function () {
      // Re-enter through the normal URL bootstrap so every filter, facet,
      // sort and page is restored without creating another history entry.
      window.location.reload();
    });
  }

  function clearCollectionQuery() {
    if (state.mode !== 'collection' || !state.query) return;

    state.query = '';
    state.collectionQueryMatched = false;
    state.collectionQueryFilterId = '';
    state.collectionKeywordSearch = false;
    state.collectionSearchQuery = '';
    state.collectionScopeFilters = [];

  }

  function buildProductFilters() {
    var filters = [];
    var keys = Object.keys(state.selectedValues);

    for (var i = 0; i < keys.length; i++) {
      var selected = state.selectedValues[keys[i]];
      if (selected && selected.input) {
        filters.push(selected.input);
      }
    }

    var min = parseFloat(state.priceMin);
    var max = parseFloat(state.priceMax);

    if (!isNaN(min) || !isNaN(max)) {
      var range = {};

      if (!isNaN(min)) range.min = min;
      if (!isNaN(max)) range.max = max;

      filters.push({ price: range });
    }

    return filters;
  }

  function normalizeCollectionQuery(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function collectionQueryMatchScore(label, query) {
    var cleanLabel = normalizeCollectionQuery(label);
    var cleanQuery = normalizeCollectionQuery(query);

    if (!cleanLabel || !cleanQuery) return -1;
    if (cleanLabel === cleanQuery) return 100;

    var compactLabel = cleanLabel.replace(/\s/g, '');
    var compactQuery = cleanQuery.replace(/\s/g, '');

    if (compactLabel === compactQuery) return 95;

    return -1;
  }

  function findCollectionQueryFilter(filters, query) {
    var rankedFilters = (filters || []).slice().sort(function (a, b) {
      return quickFilterPriority(a) - quickFilterPriority(b);
    });
    var best = null;

    for (var f = 0; f < rankedFilters.length; f++) {
      var filter = rankedFilters[f];
      if (filter.type !== 'LIST' && filter.type !== 'BOOLEAN') continue;

      var values = filter.values || [];
      var priorityPenalty = quickFilterPriority(filter);

      for (var v = 0; v < values.length; v++) {
        var value = values[v];
        if (!value.id || Number(value.count || 0) <= 0) continue;

        var score = collectionQueryMatchScore(value.label, query);
        if (score < 0) continue;

        score -= Math.min(priorityPenalty, 10);

        if (!best || score > best.score) {
          best = {
            value: value,
            score: score
          };
        }
      }
    }

    return best ? best.value : null;
  }

  function collectionDepartment() {
    var handle = String(state.collectionHandle || '').toLowerCase();

    if (handle.indexOf('womens-') === 0) return 'Women';
    if (handle.indexOf('mens-') === 0) return 'Men';
    if (handle.indexOf('girls-') === 0) return 'Girls';
    if (handle.indexOf('boys-') === 0) return 'Boys';
    if (handle.indexOf('baby-') === 0) return 'Baby';
    return '';
  }

  function escapeSearchQueryValue(value) {
    return String(value || '')
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
  }

  function collectionProductTypeQuery(products) {
    var seen = {};
    var types = [];

    for (var p = 0; p < (products || []).length; p++) {
      var type = String(products[p].productType || '').trim();
      var key = type.toLowerCase();

      if (!type || seen[key]) continue;
      seen[key] = true;
      types.push(type);
    }

    if (!types.length) return '';

    var clauses = types.map(function (type) {
      return 'product_type:"' + escapeSearchQueryValue(type) + '"';
    });

    return clauses.length === 1
      ? clauses[0]
      : '(' + clauses.join(' OR ') + ')';
  }

  function buildCollectionSearchScope(filters, products) {
    var scope = [];
    var department = collectionDepartment();
    var departmentMatched = false;

    for (var f = 0; f < (filters || []).length; f++) {
      var filter = filters[f];
      var filterLabel = normalizeCollectionQuery(filter.label);
      var values = filter.values || [];

      if (filterLabel === 'type' || filterLabel === 'product type') {
        for (var t = 0; t < values.length; t++) {
          if (Number(values[t].count || 0) > 0) {
            scope.push(normalizeFilterInput(values[t].input));
          }
        }
      }

      if (filterLabel === 'department' && department) {
        for (var d = 0; d < values.length; d++) {
          if (
            normalizeCollectionQuery(values[d].label) ===
            normalizeCollectionQuery(department)
          ) {
            scope.push(normalizeFilterInput(values[d].input));
            departmentMatched = true;
            break;
          }
        }
      }
    }

    var productTypeQuery = collectionProductTypeQuery(products);
    state.collectionSearchQuery = state.query;

    if (productTypeQuery) {
      state.collectionSearchQuery += ' ' + productTypeQuery;
    }

    if (department && !departmentMatched && !productTypeQuery) {
      state.collectionSearchQuery += ' ' + department;
    }

    return scope.filter(function (input) {
      return input && Object.keys(input).length > 0;
    });
  }

  function applyCollectionQueryFilter(filters, products) {
    if (
      state.mode !== 'collection' ||
      !state.query ||
      state.collectionQueryAttempted
    ) {
      return false;
    }

    state.collectionQueryAttempted = true;

    var match = findCollectionQueryFilter(filters, state.query);
    if (!match) {
      state.collectionKeywordSearch = true;
      state.collectionScopeFilters = buildCollectionSearchScope(filters, products);
      resetPagination();

      console.info(
        '[Preworn Browse] Using keyword search scoped by collection taxonomy:',
        state.query
      );
      return true;
    }

    state.selectedValues[match.id] = {
      input: normalizeFilterInput(match.input),
      label: match.label
    };
    state.collectionQueryMatched = true;
    state.collectionQueryFilterId = match.id;
    resetPagination();
    return true;
  }

  function catalogFilterParameter(facetId) {
    var map = {
      department: 'department',
      navigation_category: 'category',
      item_type: 'type',
      style: 'style',
      vendor: 'vendor',
      size_value: 'size',
      colour: 'colour',
      condition_name: 'condition',
      material: 'material',
      pattern_name: 'pattern'
    };

    return map[facetId] || '';
  }

  function catalogSortValue() {
    if (state.sortKey === 'TRENDING') return 'trending';
    if (state.sortKey === 'PRICE') {
      return state.reverse ? 'price_desc' : 'price_asc';
    }
    if (state.sortKey === 'CREATED') return 'newest';
    return 'featured';
  }

  function appendCatalogFilters(parameters) {
    var grouped = {};
    var keys = Object.keys(state.selectedValues);

    for (var i = 0; i < keys.length; i++) {
      var selected = state.selectedValues[keys[i]];
      var input = normalizeFilterInput(selected ? selected.input : null);
      var parameter = input.catalogParameter || '';
      var value = input.catalogValue;

      // This also lets the DB endpoint recover if a Storefront fallback was
      // used before the catalogue API became available again.
      if (!parameter && input.productVendor) {
        parameter = 'vendor';
        value = input.productVendor;
      } else if (!parameter && input.productType) {
        parameter = 'type';
        value = input.productType;
      } else if (!parameter && input.variantOption &&
          String(input.variantOption.name || '').toLowerCase() === 'size') {
        parameter = 'size';
        value = input.variantOption.value;
      }

      if (!parameter || value === null || typeof value === 'undefined') continue;
      if (!grouped[parameter]) grouped[parameter] = [];
      grouped[parameter].push(String(value));
    }

    Object.keys(grouped).forEach(function (parameter) {
      parameters.set(parameter, grouped[parameter].join(','));
    });

    if (state.priceMin !== '') parameters.set('price_min', state.priceMin);
    if (state.priceMax !== '') parameters.set('price_max', state.priceMax);
  }

  function structuredFacetScopeFromTags(tags) {
    var namespaces = {
      department: 'department',
      category: 'category',
      'navigation category': 'category',
      type: 'type',
      'item type': 'type',
      style: 'style',
      brand: 'vendor',
      vendor: 'vendor',
      colour: 'colour',
      color: 'colour',
      material: 'material',
      pattern: 'pattern',
      condition: 'condition'
    };
    var scope = {};
    var list = Array.isArray(tags) ? tags : [];
    for (var i = 0; i < list.length; i++) {
      var tag = String(list[i] || '').trim();
      var separator = tag.indexOf('_');
      if (separator < 1 || separator >= tag.length - 1) return null;
      var namespace = tag.slice(0, separator).replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ').trim().toLowerCase();
      var parameter = namespaces[namespace];
      var value = tag.slice(separator + 1).replace(/_/g, ' ').trim();
      if (!parameter || !value || Object.prototype.hasOwnProperty.call(scope, parameter)) {
        return null;
      }
      scope[parameter] = value;
    }
    return list.length ? scope : null;
  }

  function adaptCatalogFacets(facets) {
    var filters = [];

    for (var i = 0; i < (facets || []).length; i++) {
      var facet = facets[i] || {};
      var parameter = catalogFilterParameter(facet.id);
      if (!parameter) continue;

      var values = [];
      for (var v = 0; v < (facet.values || []).length; v++) {
        var sourceValue = facet.values[v] || {};
        var value = String(sourceValue.value || sourceValue.label || '').trim();
        if (!value) continue;

        values.push({
          id: 'catalog:' + parameter + ':' + value.toLowerCase(),
          label: String(sourceValue.label || value),
          count: Math.max(0, Number(sourceValue.count || 0)),
          input: {
            catalogParameter: parameter,
            catalogValue: value
          }
        });
      }

      if (!values.length) continue;

      filters.push({
        id: 'catalog:' + parameter,
        label: String(facet.label || facet.id || 'Filter'),
        type: 'LIST',
        presentation: 'TEXT',
        values: values
      });
    }

    filters.push({
      id: 'catalog:price',
      label: 'Price',
      type: 'PRICE_RANGE',
      presentation: 'TEXT',
      values: []
    });

    return filters;
  }

  async function loadCatalogResult(pageNumber, options) {
    options = options || {};

    var endpoint = app.getAttribute('data-catalog-api') || CONFIG.catalogApiUrl;
    if (!endpoint) throw new Error('The catalogue API URL is not configured.');

    var includeFacets = typeof options.includeFacets === 'boolean'
      ? options.includeFacets
      : pageNumber === 1 && state.catalogFilters.length === 0;
    var requestTimeout = Number(options.timeout || CONFIG.catalogRequestTimeout);

    var url = new URL(endpoint, window.location.href);
    url.searchParams.set('action', 'search');
    url.searchParams.set('page', String(pageNumber));
    url.searchParams.set('limit', String(CONFIG.pageSize));
    url.searchParams.set('sort', catalogSortValue());
    url.searchParams.set('include_facets', includeFacets ? '1' : '0');
    if (options.facetsOnly) url.searchParams.set('facets_only', '1');

    // Recognised namespace tags and these catalogue columns share the same
    // metafield source. Use the indexed columns for both products and facets;
    // tag-only/custom namespaces continue through exact membership below.
    var structuredTagScope = structuredFacetScopeFromTags(state.tagQueries);

    if (state.similarSku) {
      url.searchParams.set('similar_sku', state.similarSku);
      url.searchParams.set('q', state.similarTitle || state.query || 'Preloved fashion');
    } else if (state.tagQueries.length) {
      // Keep the human-readable label out of full-text search: membership is
      // always the complete exact-tag scope supplied by `tag` or `tags[]`.
      if (!structuredTagScope) {
        if (state.tagQueries.length === 1) {
          url.searchParams.set('tag', state.tagQueries[0]);
        } else {
          state.tagQueries.forEach(function (tag) {
            url.searchParams.append('tags[]', tag);
          });
        }
      }
      if (state.tagLabel) url.searchParams.set('tag_label', state.tagLabel);
    } else if (state.brandQuery) {
      // `brand` supplies the brand-scoped demand signal used by the trending
      // sorter; `vendor` remains the catalogue filter that constrains results.
      url.searchParams.set('brand', state.brandQuery);
      url.searchParams.set('vendor', state.brandQuery);
    } else if (state.query && !state.trending) {
      url.searchParams.set('q', state.query);
    }
    if (state.mode === 'collection' && state.collectionHandle) {
      url.searchParams.set('collection', state.collectionHandle);
    }
    appendCatalogFilters(url.searchParams);
    if (structuredTagScope) {
      Object.keys(structuredTagScope).forEach(function (parameter) {
        url.searchParams.set(parameter, structuredTagScope[parameter]);
      });
    }

    var controller = typeof window.AbortController === 'function'
      ? new window.AbortController() : null;
    var timeout = controller ? window.setTimeout(function () {
      controller.abort();
    }, requestTimeout) : null;
    var response;

    try {
      response = await fetch(url.toString(), {
        method: 'GET',
        mode: 'cors',
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      });
    } finally {
      if (timeout) window.clearTimeout(timeout);
    }

    var payload = await response.json();
    if (!response.ok) {
      if (
        response.status === 503 &&
        payload && payload.error === 'catalogue_busy' &&
        !options.busyRetry
      ) {
        var retrySeconds = Math.max(1, Math.min(5, Number(payload.retryAfter) || 1));
        await new Promise(function (resolve) {
          window.setTimeout(resolve, retrySeconds * 1000);
        });
        var retryOptions = {};
        Object.keys(options).forEach(function (key) {
          retryOptions[key] = options[key];
        });
        retryOptions.busyRetry = true;
        return loadCatalogResult(pageNumber, retryOptions);
      }
      throw new Error(
        'Catalogue API HTTP ' + response.status + ': ' +
        String(payload.error || 'request_failed')
      );
    }

    if (!payload || payload.source !== 'preworn_catalog_index' ||
        !Array.isArray(payload.items)) {
      throw new Error('The catalogue API returned an invalid response.');
    }

    if (Array.isArray(payload.facets) && payload.facets.length) {
      state.catalogFilters = adaptCatalogFacets(payload.facets);
    }

    return {
      catalogSource: true,
      totalCount: Math.max(0, Number(payload.totalCount || 0)),
      totalPages: Math.max(0, Number(payload.totalPages || 0)),
      pageInfo: {
        hasNextPage: !!payload.hasNextPage,
        hasPreviousPage: !!payload.hasPreviousPage,
        startCursor: null,
        endCursor: null
      },
      productFilters: state.catalogFilters,
      nodes: payload.items
    };
  }

  function loadDeferredCatalogFacets() {
    if (state.catalogFacetsLoading || state.catalogFilters.length) return;

    if (Date.now() < state.catalogUnavailableUntil) {
      var retryDelay = Math.max(
        250,
        state.catalogUnavailableUntil - Date.now() + 50
      );
      var waitingBody = document.getElementById('PrewornSearchFiltersBody');
      state.catalogFacetsDeferred = true;
      if (waitingBody) {
        waitingBody.innerHTML =
          '<p class="pw-search__muted">Loading filters…</p>';
      }
      if (state.catalogFacetRetryTimer) {
        window.clearTimeout(state.catalogFacetRetryTimer);
      }
      state.catalogFacetRetryTimer = window.setTimeout(function () {
        state.catalogFacetRetryTimer = null;
        loadDeferredCatalogFacets();
      }, retryDelay);
      return;
    }

    state.catalogFacetsLoading = true;
    loadCatalogResult(1, {
      includeFacets: true,
      facetsOnly: true,
      // The unfiltered All catalogue aggregates ten facets across more than
      // 500k products and can be slower immediately after a database restart.
      timeout: 30000
    }).then(function (result) {
      state.catalogFacetsDeferred = false;
      state.catalogFacetRetryCount = 0;
      var filters = result.productFilters || [];
      renderQuickFilters(filters);
      renderFilters(filters);
      renderActiveFilters();
    }).catch(function (error) {
      console.warn(
        '[Preworn Browse] Catalogue filters are temporarily unavailable.',
        error
      );
      var body = document.getElementById('PrewornSearchFiltersBody');

      // A heavy facet request must never retry automatically and multiply the
      // load while MariaDB is recovering. Product results remain usable and
      // the customer can explicitly retry the optional filters.
      state.catalogFacetRetryCount++;
      state.catalogFacetsDeferred = false;
      if (body && !state.catalogFilters.length) {
        body.innerHTML =
          '<p class="pw-search__muted">Filters could not load. ' +
          '<button type="button" class="link underlined-link" ' +
          'data-pw-retry-facets>Try again</button></p>';
        var retryButton = body.querySelector('[data-pw-retry-facets]');
        if (retryButton) {
          retryButton.addEventListener('click', function () {
            state.catalogFacetRetryCount = 0;
            state.catalogFacetsDeferred = true;
            body.innerHTML =
              '<p class="pw-search__muted">Loading filters…</p>';
            loadDeferredCatalogFacets();
          });
        }
      }
    }).finally(function () {
      state.catalogFacetsLoading = false;
    });
  }

  function buildStorefrontProductFilters() {
    return buildProductFilters().map(function (input) {
      if (!input || !input.catalogParameter) return input;
      if (input.catalogParameter === 'vendor') {
        return { productVendor: input.catalogValue };
      }
      if (input.catalogParameter === 'type') {
        return { productType: input.catalogValue };
      }
      return null;
    }).filter(function (input) {
      return !!input;
    });
  }

  async function loadStorefrontResult(afterCursor) {
    if (!CONFIG.storefrontToken ||
        CONFIG.storefrontToken === 'PASTE_PUBLIC_STOREFRONT_TOKEN_HERE') {
      throw new Error('The Shopify fallback is not configured.');
    }

    var graphqlQuery;
    var variables;
    var storefrontFilters = buildStorefrontProductFilters();

    if (state.mode === 'collection' && !state.collectionKeywordSearch) {
      graphqlQuery = COLLECTION_QUERY;
      variables = {
        handle: state.collectionHandle,
        first: CONFIG.pageSize,
        after: afterCursor || null,
        filters: storefrontFilters,
        sortKey: state.sortKey === 'PRICE' || state.sortKey === 'CREATED'
          ? state.sortKey
          : 'COLLECTION_DEFAULT',
        reverse: state.reverse
      };
    } else {
      graphqlQuery = SEARCH_QUERY;
      variables = {
        query: state.collectionKeywordSearch
          ? state.collectionSearchQuery
          : (state.query || '*'),
        first: CONFIG.pageSize,
        after: afterCursor || null,
        filters: state.collectionKeywordSearch
          ? state.collectionScopeFilters.concat(storefrontFilters)
          : storefrontFilters,
        sortKey: state.collectionKeywordSearch && state.sortKey === 'CREATED'
          ? 'CREATED_AT'
          : state.collectionKeywordSearch && state.sortKey === 'COLLECTION_DEFAULT'
            ? 'RELEVANCE'
            : (state.sortKey === 'TRENDING' ? 'CREATED_AT' : state.sortKey),
        reverse: state.reverse
      };
    }

    var response = await fetch(
      'https://' + CONFIG.shopDomain + '/api/' + CONFIG.apiVersion + '/graphql.json',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken
        },
        body: JSON.stringify({ query: graphqlQuery, variables: variables })
      }
    );
    var payload = await response.json();

    if (!response.ok) {
      throw new Error('HTTP ' + response.status + ': ' + JSON.stringify(payload));
    }
    if (payload.errors && payload.errors.length) {
      throw new Error(payload.errors.map(function (error) {
        return error.message;
      }).join(' | '));
    }
    if (!payload.data) throw new Error('Shopify returned no browse data.');

    if (state.mode === 'collection' && !state.collectionKeywordSearch) {
      if (!payload.data.collection) throw new Error('Collection not found.');
      return payload.data.collection.products;
    }
    if (!payload.data.search) throw new Error('Shopify returned no search data.');
    return payload.data.search;
  }

  function isUnfilteredCollectionCatalogMiss(result, pageNumber) {
    if (
      state.mode !== 'collection' ||
      state.collectionKeywordSearch ||
      Number(pageNumber) !== 1 ||
      !result ||
      !result.catalogSource ||
      Number(result.totalCount || 0) !== 0
    ) {
      return false;
    }

    return !state.query &&
      Object.keys(state.selectedValues).length === 0 &&
      !state.priceMin &&
      !state.priceMax;
  }

  async function loadPage(pageNumber, afterCursor) {
    if (state.loading) {
      // Keep the latest multi-select state. Once the in-flight request ends,
      // run one fresh request containing every option selected in the meantime.
      state.pendingReload = true;
      return;
    }

    var reloadWithCollectionQuery = false;
    var runPendingReload = false;
    state.pendingReload = false;
    state.loading = true;
    setLoading(true);
    clearError();

    try {
      var result;
      var source = 'Shopify Storefront API';
      var hadCatalogResult = state.usingCatalog ||
        !!(state.lastResult && state.lastResult.catalogSource);

      if (
        !CONFIG.catalogApiEnabled ||
        (state.mode === 'collection' && state.collectionCatalogStale)
      ) {
        // Avoid touching a known-unhealthy cross-origin endpoint. An aborted
        // fetch is correctly shown in red by DevTools even when fallback works.
        state.usingCatalog = false;
        state.catalogFacetsDeferred = false;
        if (state.collectionCatalogStale) {
          source = 'Shopify Storefront API collection fallback';
        }
        result = await loadStorefrontResult(afterCursor);
      } else {
        try {
          if (Date.now() < state.catalogUnavailableUntil) {
            throw new Error('Catalogue API is in temporary failover mode.');
          }

          result = await loadCatalogResult(pageNumber, {
            includeFacets: false,
            timeout: CONFIG.catalogRequestTimeout
          });
          if (isUnfilteredCollectionCatalogMiss(result, pageNumber)) {
            // Shopify evaluates newly created automated collections
            // immediately, while the separate MySQL membership projection is
            // reconciled by the next bulk catalogue sync. Never let a valid
            // but stale zero replace Shopify's authoritative collection.
            state.collectionCatalogStale = true;
            result = null;
            throw new Error(
              'Catalogue collection membership is stale; verifying with Shopify.'
            );
          }
          state.catalogUnavailableUntil = 0;
          source = 'PREWORN catalogue database';
          if (pageNumber === 1 && state.catalogFilters.length === 0) {
            state.catalogFacetsDeferred = true;
          }
        } catch (catalogError) {
          state.catalogUnavailableUntil = Date.now() + CONFIG.catalogFailureCooldown;

          if (!result) {
            if (hadCatalogResult) throw catalogError;

            state.usingCatalog = false;
            console.warn(
              '[Preworn Browse] Catalogue API unavailable; using Shopify fallback.',
              catalogError
            );
            source = 'Shopify Storefront API fallback';
            result = await loadStorefrontResult(afterCursor);
          }
        }
      }

      if (result.catalogSource) {
        state.usingCatalog = true;
        state.collectionKeywordSearch = false;
        state.collectionSearchQuery = '';
        state.collectionScopeFilters = [];
      }

      var availableFilters = result.productFilters || result.filters || [];
      if (
        !result.catalogSource &&
        pageNumber === 1 &&
        applyCollectionQueryFilter(availableFilters, result.nodes || [])
      ) {
        reloadWithCollectionQuery = true;
      }

      window.PrewornSearchDebug = {
        source: source,
        mode: state.mode,
        page: pageNumber,
        query: state.query,
        collectionHandle: state.collectionHandle,
        totalCount: result.totalCount,
        totalPages: result.totalPages,
        productsLoaded: result.nodes ? result.nodes.length : 0,
        filterCount: availableFilters.length,
        hasNextPage: result.pageInfo ? result.pageInfo.hasNextPage : false,
        startCursor: result.pageInfo ? result.pageInfo.startCursor : null,
        endCursor: result.pageInfo ? result.pageInfo.endCursor : null
      };

      console.log('[PREWORN API]', window.PrewornSearchDebug);
      if (!reloadWithCollectionQuery) {
        state.currentPage = pageNumber;
        state.lastResult = result;

        if (result.pageInfo && result.pageInfo.hasNextPage &&
            result.pageInfo.endCursor) {
          state.pageCursors[pageNumber + 1] = result.pageInfo.endCursor;
        }

        renderResult(result);
        hideNativeBrowse();
        scrollResultsIntoView(pageNumber);

        if (
          result.catalogSource &&
          pageNumber === 1 &&
          state.catalogFacetsDeferred
        ) {
          loadDeferredCatalogFacets();
        }
      }
    } catch (error) {
      console.error('[Preworn Browse]', error);

      if (hadCatalogResult) {
        showError(
          'The catalogue request took too long. Your previous database results have been kept; please try the filter again.'
        );
        hideNativeBrowse();
      } else {
        showError(
          'The enhanced product browser could not load, so the normal Shopify results are available below. ' +
          error.message
        );
        showNativeFallback();
      }
    } finally {
      state.loading = false;
      setLoading(false);
      runPendingReload = state.pendingReload;
      state.pendingReload = false;
    }

    if (reloadWithCollectionQuery || runPendingReload) loadPage(1, null);
  }

  function renderResult(result) {
    var availableFilters = result.productFilters || result.filters || [];
    var visibleFilters = availableFilters;

    if (state.collectionKeywordSearch) {
      visibleFilters = availableFilters.filter(function (filter) {
        var label = normalizeCollectionQuery(filter.label);
        return label !== 'type' &&
          label !== 'product type' &&
          label !== 'department';
      });
    }

    renderCount(
      result.totalCount,
      availableFilters,
      result.nodes || [],
      result.pageInfo || {},
      result
    );
    renderProducts(result.nodes || []);
    renderQuickFilters(visibleFilters);
    renderFilters(visibleFilters);
    renderActiveFilters();
    renderPagination(result);
  }

  function renderCount(totalCount, filters, products, pageInfo, result) {
    var count = document.getElementById('PrewornSearchCount');
    var toolbarCount = document.getElementById('PrewornToolbarCount');
    if (!count && !toolbarCount) return;

    var description;
    var toolbarLabel;

    if (result && result.catalogSource) {
      var exactTotal = Math.max(0, Number(totalCount || 0));
      var exactDisplay = exactTotal.toLocaleString('en-GB');
      var isMatching = hasActiveFilters() ||
        (state.mode === 'collection' && !!state.query);

      description = exactDisplay + (isMatching
        ? ' matching preloved '
        : ' unique preloved ') + (exactTotal === 1 ? 'piece' : 'pieces');
      toolbarLabel = exactDisplay + ' ' +
        (exactTotal === 1 ? 'item' : 'items');
    } else if (state.mode === 'collection' && state.collectionKeywordSearch) {
      var keywordTotal = Math.min(
        Number(totalCount || 0),
        CONFIG.shopifyFallbackMaxResults
      );
      var keywordDisplay = Number(totalCount || 0) > CONFIG.shopifyFallbackMaxResults
        ? CONFIG.shopifyFallbackMaxResults.toLocaleString('en-GB') + '+'
        : keywordTotal.toLocaleString('en-GB');

      description = keywordDisplay + ' matching preloved ' +
        (keywordTotal === 1 ? 'piece' : 'pieces');
      toolbarLabel = keywordDisplay + ' ' +
        (keywordTotal === 1 ? 'item' : 'items');
    } else if (state.mode === 'collection') {
      if (hasActiveFilters()) {
        var filteredCount = getFilteredCollectionCount(filters, products, pageInfo);

        if (filteredCount !== null) {
          var filteredDisplay = filteredCount.toLocaleString('en-GB');
          description = filteredDisplay + ' matching preloved ' +
            (filteredCount === 1 ? 'piece' : 'pieces');
          toolbarLabel = filteredDisplay + ' ' +
            (filteredCount === 1 ? 'item' : 'items');
        } else {
          description = 'A filtered selection of unique preloved pieces';
          toolbarLabel = 'Filtered items';
        }
      } else if (
        typeof state.collectionCount === 'number' &&
        isFinite(state.collectionCount)
      ) {
        var collectionDisplay = state.collectionCount > CONFIG.shopifyFallbackMaxResults
          ? CONFIG.shopifyFallbackMaxResults.toLocaleString('en-GB') + '+'
          : state.collectionCount.toLocaleString('en-GB');

        description = collectionDisplay + ' unique preloved pieces';
        toolbarLabel = collectionDisplay + ' items';
      } else {
        description = 'Unique preloved pieces';
        toolbarLabel = 'Items';
      }
    } else {
      var shown = totalCount > CONFIG.shopifyFallbackMaxResults
        ? CONFIG.shopifyFallbackMaxResults.toLocaleString('en-GB') + '+'
        : Number(totalCount || 0).toLocaleString('en-GB');

      description = shown + ' matching preloved pieces';
      toolbarLabel = shown + ' items';
    }

    if (count) count.textContent = description;
    if (toolbarCount) toolbarCount.textContent = toolbarLabel;
  }

  function getFilteredCollectionCount(filters, products, pageInfo) {
    var singleValueGroups = ['type', 'product type', 'brand', 'vendor'];

    for (var g = 0; g < singleValueGroups.length; g++) {
      for (var f = 0; f < filters.length; f++) {
        var filter = filters[f];
        var label = String(filter.label || '').toLowerCase().trim();

        if (label !== singleValueGroups[g]) continue;

        var values = filter.values || [];
        var containsSelectedValue = values.some(function (value) {
          return !!state.selectedValues[value.id];
        });

        if (!values.length || containsSelectedValue) continue;

        var total = values.reduce(function (sum, value) {
          return sum + Math.max(0, Number(value.count || 0));
        }, 0);

        if (total > 0) return total;
      }
    }

    if (!pageInfo.hasNextPage && state.currentPage === 1) {
      return products.length;
    }

    return null;
  }

  function renderProducts(products) {
    var grid = document.getElementById('PrewornSearchGrid');
    if (!grid) return;

    if (!products.length) {
      grid.innerHTML =
        '<li class="pw-search__empty">No products match these filters.</li>';
      return;
    }

    var html = '';
    var wishlistIndex = getWishlistIndex();

    for (var i = 0; i < products.length; i++) {
      html += productCard(products[i], wishlistIndex);
    }

    grid.innerHTML = html;
    bindWishlistButtons(grid);
  }

  function readWishlist() {
    try {
      var stored = window.localStorage.getItem(CONFIG.wishlistStorageKey);
      var parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[Preworn Wishlist] Saved items could not be read.', error);
      return [];
    }
  }

  function writeWishlist(items) {
    try {
      window.localStorage.setItem(CONFIG.wishlistStorageKey, JSON.stringify(items));
      return true;
    } catch (error) {
      console.warn('[Preworn Wishlist] Saved items could not be updated.', error);
      return false;
    }
  }

  function getWishlistIndex() {
    var items = readWishlist();
    var index = {};

    for (var i = 0; i < items.length; i++) {
      if (items[i] && items[i].id) index[String(items[i].id)] = true;
    }

    return index;
  }

  function bindWishlistButtons(scope) {
    var buttons = scope.querySelectorAll('[data-pw-wishlist]');

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function () {
        var rawItem = this.getAttribute('data-pw-wishlist-item');
        var item;

        try {
          item = JSON.parse(rawItem);
        } catch (error) {
          console.warn('[Preworn Wishlist] Invalid product data.', error);
          return;
        }

        var items = readWishlist();
        var itemId = String(item.id);
        var existingIndex = -1;

        for (var w = 0; w < items.length; w++) {
          if (items[w] && String(items[w].id) === itemId) {
            existingIndex = w;
            break;
          }
        }

        var isSaved = existingIndex === -1;
        if (isSaved) {
          items.push(item);
        } else {
          items.splice(existingIndex, 1);
        }

        if (!writeWishlist(items)) return;

        syncWishlistButtons(itemId, isSaved);

        if (typeof window.CustomEvent === 'function') {
          window.dispatchEvent(new CustomEvent('preworn:wishlist-change', {
            detail: { item: item, saved: isSaved, count: items.length }
          }));
        }
      });
    }
  }

  function syncWishlistButtons(itemId, isSaved) {
    var buttons = document.querySelectorAll('[data-pw-wishlist-id="' + cssSelectorValue(itemId) + '"]');

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].classList.toggle('is-saved', isSaved);
      buttons[i].setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      buttons[i].setAttribute(
        'aria-label',
        isSaved ? 'Remove from wishlist' : 'Add to wishlist'
      );
      buttons[i].setAttribute(
        'title',
        isSaved ? 'Remove from wishlist' : 'Add to wishlist'
      );
    }
  }

  function cssSelectorValue(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/(["\\])/g, '\\$1');
  }
function getTieredProductPrice(rawPrice) {
  var originalPrice = parseFloat(rawPrice);

  if (!isFinite(originalPrice)) {
    return null;
  }

  var discountPercent = 55;
  var multiplier = 0.45;

  if (originalPrice <= 5) {
    discountPercent = 10;
    multiplier = 0.90;
  }
  else if (originalPrice <= 10) {
    discountPercent = 20;
    multiplier = 0.80;
  }
  else if (originalPrice <= 15) {
    discountPercent = 30;
    multiplier = 0.70;
  }
  else if (originalPrice <= 20) {
    discountPercent = 40;
    multiplier = 0.60;
  }
  else if (originalPrice < 30) {
    discountPercent = 45;
    multiplier = 0.55;
  }

  var discountedPrice =
    Math.round((originalPrice * multiplier) * 100) / 100;

  return {
    original: originalPrice,
    discounted: discountedPrice,
    discountPercent: discountPercent
  };
}

function productPresentation(product) {
  var vendor = String(product.vendor || '').trim();
  var title = String(product.title || '').trim();
  var attributes = product.attributes || {};
  var exactType = String(attributes.type || '').trim();
  var exactStyle = String(attributes.style || '').trim();
  var effectiveType = String(exactType || product.productType || '').trim();
  var working = title;
  var size = String(attributes.size || '').trim();
  var colour = String(attributes.colour || '').trim();

  if (vendor) {
    working = working.replace(
      new RegExp('^' + escapeRegExp(vendor) + '\\s*[-–—:]?\\s*', 'i'),
      ''
    );
  }

  working = working
    .replace(/\b(?:women|men|girl|boy)(?:['’]s|s)?\b/gi, ' ')
    .replace(/\bunisex\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  var explicitSize = size ? null : working.match(/\bsize\s+([a-z0-9./+-]+)/i);
  var looseSize = size ? null : working.match(/\b(6XL|5XL|4XL|3XL|2XL|1XL|XXXL|XXL|XL|L|M|S|XS|XXS|small|medium|large)\b/i);
  var sizeMatch = explicitSize || looseSize;

  if (sizeMatch) {
    size = sizeMatch[1];
    working = working.replace(sizeMatch[0], ' ');
  } else if (size) {
    working = working
      .replace(new RegExp('\\bsize\\s+' + escapeRegExp(size) + '\\b', 'i'), ' ')
      .replace(new RegExp('\\b' + escapeRegExp(size) + '\\b', 'i'), ' ');
  }

  var colours = [
    'Black', 'White', 'Grey', 'Gray', 'Navy', 'Blue', 'Green', 'Red',
    'Pink', 'Purple', 'Orange', 'Yellow', 'Brown', 'Beige', 'Cream',
    'Burgundy', 'Khaki', 'Teal', 'Multi', 'Silver', 'Gold'
  ];

  if (colour) {
    working = working.replace(
      new RegExp('\\b' + escapeRegExp(colour) + '\\b', 'i'),
      ' '
    );
  } else {
    for (var c = 0; c < colours.length; c++) {
      var colourPattern = new RegExp('\\b' + colours[c] + '\\b', 'i');
      if (colourPattern.test(working)) {
        colour = colours[c] === 'Gray' ? 'Grey' : colours[c];
        working = working.replace(colourPattern, ' ');
        break;
      }
    }
  }

  working = working
    .replace(/\s*[-–—|:]\s*/g, ' ')
    .replace(/[‘’']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // A large number of older products have a useful item type but no stored
  // Style metafield. Do not let the generic type (for example "Dress") hide
  // a clear style in the product title. The same shared presentation is used
  // by search, collections, homepage rows and Shop similar.
  var retailTitle = exactStyle
    ? styleAndTypeTitle(exactStyle, exactType)
    : conciseRetailTitle(working, effectiveType, '');
  if (!retailTitle) retailTitle = exactType || effectiveType || 'Preloved piece';

  var meta = [];
  if (size) {
    var displaySize = String(size).toUpperCase();
    if (displaySize === 'SMALL') displaySize = 'S';
    if (displaySize === 'MEDIUM') displaySize = 'M';
    if (displaySize === 'LARGE') displaySize = 'L';
    meta.push('Size ' + displaySize);
  }
  if (colour) meta.push(colour);

  return {
    title: retailTitle,
    meta: meta.join(' · ')
  };
}

function styleAndTypeTitle(style, type) {
  var styleValue = String(style || '').replace(/\s+/g, ' ').trim();
  var typeValue = String(type || '').replace(/\s+/g, ' ').trim();

  if (!styleValue) return typeValue || 'Preloved piece';
  if (!typeValue) return styleValue;

  var styleKey = styleValue.toLowerCase().replace(/[^a-z0-9]+/g, '');
  var typeKey = typeValue.toLowerCase().replace(/[^a-z0-9]+/g, '');

  if (styleKey === typeKey || styleKey.indexOf(typeKey) !== -1) {
    return styleValue;
  }
  if (typeKey.indexOf(styleKey) !== -1) {
    return typeValue;
  }

  return styleValue + ' ' + typeValue;
}

function conciseRetailTitle(title, productType, productStyle) {
  var source = String(title || '').trim();
  var titleSearchable = source.toLowerCase();
  var productTypeSearchable = String(productType || '').toLowerCase();
  var searchable = (
    source + ' ' + String(productType || '') + ' ' + String(productStyle || '')
  ).toLowerCase();
  var typeDefinitions = [
    ['Hoodie', /\bhoodies?\b/],
    ['Sweatshirt', /\bsweatshirts?\b/],
    ['Cardigan', /\bcardigans?\b/],
    ['Jumper', /\bjumpers?\b/],
    ['Sweater', /\bsweaters?\b/],
    ['Fleece', /\bfleeces?\b/],
    ['Polo Shirt', /\bpolo(?:\s+shirts?)?\b/],
    ['T-Shirt', /\b(?:t[ -]?shirts?|tees?)\b/],
    ['Shirt', /\bshirts?\b/],
    ['Blouse', /\bblouses?\b/],
    ['Gilet', /\bgilets?\b/],
    ['Jacket', /\bjackets?\b/],
    ['Coat', /\bcoats?\b/],
    ['Blazer', /\bblazers?\b/],
    ['Dress', /\bdress(?:es)?\b/],
    ['Jeans', /\bjeans?\b/],
    ['Trousers', /\btrousers?\b/],
    ['Shorts', /\bshorts?\b/],
    ['Skirt', /\bskirts?\b/],
    ['Leggings', /\bleggings?\b/],
    ['Jumpsuit', /\bjumpsuits?\b/],
    ['Top', /\btops?\b/],
    ['Trainers', /\btrainers?\b/],
    ['Boots', /\bboots?\b/],
    ['Shoes', /\bshoes?\b/],
    ['Bag', /\bbags?\b/]
  ];
  var type = firstMatchedLabel(productTypeSearchable, typeDefinitions) ||
    firstMatchedLabel(titleSearchable, typeDefinitions);

  var primaryStyle = firstMatchedLabel(searchable, [
    ['Fit & Flare', /\bfit\s*(?:&|and)\s*flare\b/],
    ['Straight & Pencil', /\b(?:straight\s*(?:&|and)\s*pencil|pencil)\b/],
    ['A-Line', /\ba[ -]?line\b/],
    ['Puffer', /\bpuffer\b/],
    ['Full Zip', /\b(?:full[ -]?zip|zip[ -]?through)\b/],
    ['Quarter Zip', /\b(?:quarter[ -]?zip|1\s*\/\s*4[ -]?zip)\b/],
    ['Half Zip', /\b(?:half[ -]?zip|1\s*\/\s*2[ -]?zip)\b/],
    ['Zip Up', /\bzip[ -]?up\b/],
    ['Crew Neck', /\bcrew[ -]?neck\b/],
    ['V-Neck', /\bv[ -]?neck\b/],
    ['Roll Neck', /\broll[ -]?neck\b/],
    ['Funnel Neck', /\bfunnel[ -]?neck\b/],
    ['High Neck', /\bhigh[ -]?neck\b/],
    ['Off Shoulder', /\b(?:off[ -]?shoulder|bardot)\b/],
    ['One Shoulder', /\bone[ -]?shoulder\b/],
    ['Halter Neck', /\bhalter(?:[ -]?neck)?\b/],
    ['Wrap', /\bwrap(?:around)?\b/],
    ['Bodycon', /\bbody[ -]?con\b/],
    ['Shirt', /\bshirt[ -]?dress\b/],
    ['Shift', /\bshift\b/],
    ['Smock', /\bsmock\b/],
    ['Skater', /\bskater\b/],
    ['Swing', /\bswing\b/],
    ['Slip', /\bslip\b/],
    ['Tunic', /\btunic\b/],
    ['Tiered', /\btiered\b/],
    ['Pleated', /\bpleated?\b/],
    ['Maxi', /\bmaxi\b/],
    ['Midi', /\bmidi\b/],
    ['Mini', /\bmini\b/],
    ['Pullover', /\bpullovers?\b/],
    ['Cropped', /\bcropped\b/],
    ['Oversized', /\boversi[sz]ed\b/]
  ]);

  if (type === 'Hoodie' && primaryStyle === 'Pullover') {
    primaryStyle = '';
  }

  var detail = firstMatchedLabel(searchable, [
    ['High Neck', /\bhigh[ -]?neck\b/],
    ['Graphic Print', /\b(?:graphic(?:\s+print)?|printed)\b/],
    ['Colour Block', /\bcolou?r[ -]?block\b/],
    ['Embroidered', /\bembroider(?:ed|y)\b/],
    ['Striped', /\bstriped?\b/],
    ['Fleece', /\bfleece\b/],
    ['Logo', /\blogo\b/],
    ['Pocket', /\bpockets?\b/]
  ]);

  if (detail === type || detail === primaryStyle) detail = '';
  if (primaryStyle === 'High Neck') detail = '';

  var parts = [];
  if (primaryStyle) parts.push(primaryStyle);
  if (detail) parts.push(detail);
  if (type) parts.push(type);

  if (parts.length) return parts.join(' ');

  var fallback = String(productType || source || 'Preloved piece')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .slice(0, 5)
    .join(' ');

  return fallback || 'Preloved piece';
}

function firstMatchedLabel(source, definitions) {
  for (var i = 0; i < definitions.length; i++) {
    if (definitions[i][1].test(source)) return definitions[i][0];
  }
  return '';
}

function brandLogoHash(value) {
  var source = String(value || '').trim();
  if (typeof source.normalize === 'function') source = source.normalize('NFC');
  source = source.toLowerCase();
  var hash = 2166136261;
  for (var index = 0; index < source.length; index++) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return ('00000000' + (hash >>> 0).toString(16)).slice(-8);
}

/* Shopify vendor values are not fully canonical. Map reviewed spelling,
   punctuation and sub-brand variants to the verified logo owner so every
   shared card surface requests the same uploaded PNG. */
var BRAND_LOGO_VENDOR_ALIASES = {
  'm and s': 'Marks & Spencer',
  'marks and spencer': 'Marks & Spencer',
  'm and s collection': 'Marks & Spencer',
  'marks and spencer collection': 'Marks & Spencer',
  'marks and spencer x ghost': 'Marks & Spencer',
  'dororthy perkins': 'Dorothy Perkins',
  'h and m': 'H&M',
  'h and m divided': 'H&M',
  'divided by h and m': 'H&M',
  'divided h and m': 'H&M',
  'h and m essentials': 'H&M',
  'h and m logg': 'H&M',
  'h and m mama': 'H&M',
  'h and m sport': 'H&M',
  'new look': 'New Look',
  'new look 915': 'New Look',
  'new look curves': 'New Look',
  'primark cares': 'Primark',
  'rebel by primark': 'Primark',
  'zara basic': 'Zara',
  'zara trafaluc': 'Zara',
  'zara woman': 'Zara',
  'zara man': 'Zara',
  'zara kids': 'Zara',
  'zara knit': 'Zara',
  'topshop moto': 'Topshop',
  'adidas neo': 'Adidas',
  'adidas originals': 'Adidas',
  'river island': 'River Island',
  'the collection debenhams': 'Debenhams',
  'collection debenhams': 'Debenhams',
  'debenhams petite collection': 'Debenhams',
  'disney baby': 'Disney',
  'disney store': 'Disney',
  'disney stores': 'Disney',
  'disney world': 'Disney',
  'mango basics': 'Mango',
  'mango suit': 'Mango',
  'mango jeans': 'Mango',
  'levis': "Levi's",
  'levis premium': "Levi's",
  'denizen for levis': "Levi's",
  'burton menswear': 'Burton',
  'burton menswear london': 'Burton',
  'burton london': 'Burton',
  'montague burton': 'Burton',
  'pull and bear': 'Pull&Bear',
  'jack and jones': 'Jack & Jones',
  'premium by jack and jones': 'Jack & Jones',
  'jack and jones core': 'Jack & Jones',
  'jack and jones vintage': 'Jack & Jones',
  'by very': 'Very',
  'v by very': 'Very',
  'john lewis and partners': 'John Lewis',
  'john lewis women': 'John Lewis',
  'kin by john lewis': 'John Lewis',
  'calvin klein jeans': 'Calvin Klein',
  'mini boden': 'Boden',
  'boss hugo boss': 'Hugo Boss',
  'boss': 'Hugo Boss',
  'hugo': 'Hugo Boss',
  'victorias secret': "Victoria's Secret",
  'pink victorias secret': "Victoria's Secret",
  'victorias secret pink': "Victoria's Secret",
  'lyle and scott': 'Lyle & Scott',
  'dunnes': 'Dunnes Stores',
  'dunnes stored': 'Dunnes Stores',
  'helen steele x dunnes stores': 'Dunnes Stores',
  'st bernard for dunnes stores': 'Dunnes Stores',
  'polo ralph lauren': 'Ralph Lauren',
  'lauren ralph lauren': 'Ralph Lauren',
  'chaps ralph lauren': 'Ralph Lauren',
  'polo golf ralph lauren': 'Ralph Lauren',
  'polo jeans ralph lauren': 'Ralph Lauren',
  'ralph lauren sport': 'Ralph Lauren',
  'bench': 'Bench',
  'american eagle outfitters': 'American Eagle',
  'all saints': 'AllSaints',
  'allsaints spitalfields': 'AllSaints',
  'berghaus fashion': 'Berghaus',
  'coca cola': 'Coca-Cola',
  'barbour international': 'Barbour',
  'north face': 'The North Face',
  'fat face': 'FatFace',
  'sheinside': 'SheIn',
  'morrisons': "Morrison's",
  'netflix stranger things': 'Netflix',
  'asos design': 'ASOS',
  'asos edition': 'ASOS',
  'boohoo man': 'Boohoo',
  'boohooman': 'Boohoo',
  'denim co': 'Denim & Co.',
  'denim and co': 'Denim & Co.',
  'deniem and co': 'Denim & Co.',
  'denimco': 'Denim & Co.',
  'f and f': 'F&F',
  'f and f active': 'F&F',
  'f and f kids': 'F&F',
  'f and f school': 'F&F',
  'baby gap': 'Gap',
  'babygap': 'Gap',
  'gap factory store': 'Gap',
  'gap kids': 'Gap',
  'asda george': 'George',
  'm and co': 'M&Co',
  'spirit at m and co': 'M&Co',
  'miss selfirdge': 'Miss Selfridge',
  'monsoon fusion': 'Monsoon',
  'next tailoring': 'NEXT',
  'next woman': 'NEXT',
  'pretty little thing': 'PRETTYLITTLETHING',
  'prettylitlething': 'PRETTYLITTLETHING',
  'regatta professional': 'Regatta',
  'regatta great outdoors': 'Regatta',
  'tu school': 'Tu',
  'spotlight by warehouse': 'Warehouse'
};

function brandLogoAliasKey(value) {
  var source = String(value || '').trim().toLowerCase();
  if (typeof source.normalize === 'function') {
    source = source.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  return source
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/^\s+|\s+$/g, '')
    .replace(/\s+/g, ' ');
}

function canonicalBrandLogoVendor(vendor) {
  var source = String(vendor || '').trim();
  return BRAND_LOGO_VENDOR_ALIASES[brandLogoAliasKey(source)] || source;
}

function appendBrandLogoAssetNames(names, vendor, includeLegacy) {
  var source = String(vendor || '').trim().toLowerCase();
  if (!source) return;
  var ascii = source;
  if (typeof ascii.normalize === 'function') {
    ascii = ascii.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  var slug = ascii
    .replace(/['’]/g, '')
    .replace(/&/g, '-and-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 72)
    .replace(/-+$/g, '') || 'brand';

  names.push('pw-brand-' + slug + '-' + brandLogoHash(vendor));
  if (!includeLegacy) return names;
  var legacyNames = [
    source
      .replace(/ /g, '-')
      .replace(/&/g, '-')
      .replace(/é/g, 'e')
      .replace(/\./g, ''),
    ascii
      .replace(/['’]/g, '')
      .replace(/&/g, '-')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  ];
  var unsafeLegacyAssets = {
    'planet': true,
    'baby': true,
    '11°': true
  };
  if (!unsafeLegacyAssets[source]) names = names.concat(legacyNames);
  return names;
}

function brandLogoAssetNames(vendor) {
  var requestedVendor = String(vendor || '').trim();
  if (!requestedVendor) return [];
  var canonicalVendor = canonicalBrandLogoVendor(requestedVendor);
  var names = [];
  names = appendBrandLogoAssetNames(names, canonicalVendor, true) || names;
  if (canonicalVendor.toLowerCase() !== requestedVendor.toLowerCase()) {
    names = appendBrandLogoAssetNames(names, requestedVendor, true) || names;
  }
  var unique = [];
  for (var i = 0; i < names.length; i++) {
    var filename = names[i] ? names[i] + '.png' : '';
    if (filename && unique.indexOf(filename) === -1) unique.push(filename);
  }
  return unique;
}

function brandLogoHtml(vendor) {
  var brand = String(vendor || '').trim();
  if (!brand) return '';

  var assetSource = app && app.getAttribute('data-brand-logo-base')
    ? app
    : document.querySelector('[data-brand-logo-base]');
  if (!assetSource) return '';

  var assetBase = String(assetSource.getAttribute('data-brand-logo-base') || '');
  if (!assetBase) return '';
  if (assetBase.charAt(assetBase.length - 1) !== '/') assetBase += '/';

  /* Prefer the standardized authentic PNG. Keep verified legacy artwork as a
     compatibility fallback while the canonical library is being deployed. */
  var assetNames = brandLogoAssetNames(brand);
  if (!assetNames.length) return '';
  var urls = [];
  for (var i = 0; i < assetNames.length; i++) {
    urls.push(assetBase + encodeURIComponent(assetNames[i]));
  }
  var fallback = String(assetSource.getAttribute('data-brand-logo-fallback') || '');
  var remaining = urls.slice(1);
  if (fallback && remaining.indexOf(fallback) === -1) remaining.push(fallback);

  return '<img class="pw-card__brand-logo" src="' +
    escapeAttr(urls[0]) +
    '" alt="" aria-hidden="true" loading="lazy"' +
    (fallback ? ' data-fallback="' + escapeAttr(fallback) + '"' : '') +
    ' data-logo-next="' + escapeAttr(remaining.join('|')) + '"' +
    ' onerror="var queue=(this.getAttribute(\'data-logo-next\')||\'\').split(\'|\');' +
      'var next=queue.shift();this.setAttribute(\'data-logo-next\',queue.join(\'|\'));' +
      'if(next){this.setAttribute(\'src\',next);}else{this.hidden=true;}">';
}

  function productIsNew(product) {
  var value = String(product && (
    product.createdAt || product.shopifyCreatedAt || product.publishedAt
  ) || '').trim();
  if (!value) return false;

  // MySQL catalogue timestamps are UTC but arrive without a timezone suffix.
  // Shopify fallback timestamps are already ISO-8601. Normalise both before
  // applying the same rolling window in search and collection results.
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) {
    value = value.replace(' ', 'T') + 'Z';
  } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)) {
    value += 'Z';
  }

  var timestamp = Date.parse(value);
  if (!isFinite(timestamp)) return false;
  var day = 24 * 60 * 60 * 1000;
  var age = Date.now() - timestamp;
  return age >= -day && age <= CONFIG.newProductWindowDays * day;
}

  function productCard(product, wishlistIndex) {
  var image = product.featuredImage;
  var presentation = productPresentation(product);
  var soldActivity = product.soldActivity || null;

  var price = product.priceRange &&
              product.priceRange.minVariantPrice
    ? product.priceRange.minVariantPrice
    : null;

  var href =
    product.onlineStoreUrl ||
    ('/products/' + product.handle);

  /* Keep Shopify's collection context when a product is opened from a
     collection. This makes the product page's `collection` object available
     for breadcrumbs and attribute-search links. */
  if (state.mode === 'collection' && state.collectionHandle && product.handle) {
    href = '/collections/' + encodeURIComponent(state.collectionHandle) +
      '/products/' + encodeURIComponent(product.handle);
  }

  var tierPrice = price
    ? getTieredProductPrice(price.amount)
    : null;

  var originalPrice = '';
  var discountedPrice = '';
  var discountPercent = 0;
  var saving = '';

  if (tierPrice) {
    originalPrice = money(
      tierPrice.original,
      price.currencyCode
    );

    discountedPrice = money(
      tierPrice.discounted,
      price.currencyCode
    );

    discountPercent = tierPrice.discountPercent;
    saving = money(
      tierPrice.original - tierPrice.discounted,
      price.currencyCode
    );
  }

  var imageHtml = image
    ? '<img class="pw-card__product-image" loading="lazy" src="' +
        escapeAttr(image.url) +
        '" alt="' +
        escapeAttr(image.altText || product.title) +
        '">'
    : '<div class="pw-card__no-image">No image</div>';

  var discountBadge = soldActivity
    ? '<div class="pw-card__discount pw-card__sold-badge" aria-label="Sold"><span>SOLD</span></div>'
    : '';

  var newBadge = !soldActivity && productIsNew(product)
    ? '<span class="pw-card__new-badge" aria-label="New in the last 30 days">NEW</span>'
    : '';

  if (!soldActivity && discountPercent > 0) {
    discountBadge =
      '<div class="pw-card__discount pw-discount--' + discountPercent + '" aria-label="' +
        discountPercent +
        '% off">' +

        '<span>' +
          discountPercent +
          '%</span>' +

        '<small>OFF</small>' +

      '</div>';
  }

  var wishlistId = String(product.id || product.handle || href);
  var isWishlisted = !!(wishlistIndex && wishlistIndex[wishlistId]);
  var wishlistItem = {
    id: wishlistId,
    handle: product.handle || '',
    url: href,
    vendor: product.vendor || '',
    title: presentation.title,
    meta: presentation.meta,
    image: image ? image.url : '',
    price: discountedPrice,
    originalPrice: originalPrice,
    saving: saving,
    discountPercent: discountPercent,
    category: String(
      (product.attributes && (product.attributes.type || product.attributes.category)) ||
      product.productType ||
      presentation.title ||
      ''
    ),
    collectionHandle: state.mode === 'collection' ? state.collectionHandle : '',
    collectionTitle: state.mode === 'collection' ? state.collectionTitle : ''
  };

  var actionsHtml = '';
  if (!soldActivity) {
    actionsHtml =
      '<div class="pw-card__actions">' +
        '<button type="button" class="pw-card__cart" data-pw-add-cart' +
          ' data-product-handle="' + escapeAttr(product.handle || '') + '"' +
          ' data-product-url="' + escapeAttr(href) + '"' +
          (product.availableForSale === false ? ' disabled' : '') +
          ' aria-label="' + (product.availableForSale === false ? 'Sold out' : 'Add to basket') + '"' +
          ' title="' + (product.availableForSale === false ? 'Sold out' : 'Add to basket') + '">' +
          '<svg class="pw-card__cart-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24">' +
            '<path d="M5.5 8.5h13l1 12h-15l1-12Z"></path><path d="M9 9V6.5a3 3 0 0 1 6 0V9"></path><path d="M12 12v5M9.5 14.5h5"></path>' +
          '</svg>' +
          '<svg class="pw-card__cart-check" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m6.5 12.5 3.5 3.5 7.5-8"></path></svg>' +
        '</button>' +
        '<button type="button" class="pw-card__wishlist' +
          (isWishlisted ? ' is-saved' : '') + '"' +
          ' data-pw-wishlist' +
          ' data-pw-wishlist-id="' + escapeAttr(wishlistId) + '"' +
          ' data-pw-wishlist-item="' + escapeAttr(JSON.stringify(wishlistItem)) + '"' +
          ' aria-pressed="' + (isWishlisted ? 'true' : 'false') + '"' +
          ' aria-label="' + (isWishlisted ? 'Remove from wishlist' : 'Add to wishlist') + '"' +
          ' title="' + (isWishlisted ? 'Remove from wishlist' : 'Add to wishlist') + '">' +
          '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">' +
            '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"></path>' +
          '</svg>' +
        '</button>' +
      '</div>';
  }

  return (
    '<li class="pw-card' + (soldActivity ? ' pw-card--sold' : '') + '">' +

      '<div class="pw-card__media">' +

        '<a class="pw-card__image-link" href="' + escapeAttr(href) + '"' +
          ' aria-label="' + (soldActivity ? 'Find similar to ' : 'View ') +
          escapeAttr(product.title || presentation.title) + '">' +
          newBadge +
          brandLogoHtml(product.vendor) +
          imageHtml +
          discountBadge +
        '</a>' +

        actionsHtml +

      '</div>' +

      '<a class="pw-card__link" href="' + escapeAttr(href) + '">' +

        '<div class="pw-card__body">' +

          '<div class="pw-card__vendor">' +
            escapeHtml(product.vendor || '') +
          '</div>' +

          '<div class="pw-card__title">' +
            escapeHtml(presentation.title) +
          '</div>' +

          (presentation.meta
            ? '<div class="pw-card__meta">' + escapeHtml(presentation.meta) + '</div>'
            : '') +

          (product.handle
            ? '<div class="pwdemand-slot pwdemand-slot--card" data-pw-demand data-product-handle="' + escapeAttr(product.handle) + '" data-pw-demand-context="card" hidden></div>'
            : '') +

          '<div class="pw-card__price">' +

            '<span class="pw-card__original">' +
              escapeHtml(originalPrice) +
            '</span>' +

            '<span class="pw-card__current">' +
              escapeHtml(discountedPrice) +
            '</span>' +

          '</div>' +

          (saving
            ? '<div class="pw-card__saving">Save ' + escapeHtml(saving) + '</div>'
            : '') +

          (soldActivity && soldActivity.age
            ? '<div class="pw-card__sold-context">JUST BOUGHT · ' + escapeHtml(soldActivity.age) + '</div>'
            : '') +

        '</div>' +

      '</a>' +

    '</li>'
  );
}

  function renderQuickFilters(filters) {
    var target = document.getElementById('PrewornQuickFilters');
    if (!target) return;

    var valueLimit = Math.max(1, CONFIG.maxQuickFilters - 2);
    var definitions = [
      { label: 'Hoodies', terms: ['hoodie'] },
      { label: 'Sweatshirts', terms: ['sweatshirt', 'sweat'] },
      { label: 'Quarter Zip', terms: ['quarter zip', '1/4 zip', 'quarter-zip'] },
      { label: 'Fleece', terms: ['fleece'] },
      { label: 'Nike', terms: ['nike'] },
      { label: 'Adidas', terms: ['adidas'] },
      { label: 'The North Face', terms: ['the north face', 'north face'] }
    ];
    var used = {};
    var usedLabels = {};
    var quickValues = [];

    var primaryFilters = filters.slice().sort(function (a, b) {
      return quickFilterPriority(a) - quickFilterPriority(b);
    }).filter(function (filter) {
      return quickFilterPriority(filter) < 10;
    });

    for (var d = 0; d < definitions.length; d++) {
      var match = findQuickFilterValue(primaryFilters, definitions[d].terms, used);
      if (!match) continue;

      addQuickFilterValue(
        quickValues,
        match,
        definitions[d].label,
        used,
        usedLabels,
        valueLimit
      );
    }

    for (var f = 0; f < primaryFilters.length && quickValues.length < valueLimit; f++) {
      var filter = primaryFilters[f];
      var priority = quickFilterPriority(filter);
      var groupLimit = priority <= 1 ? 4 : priority === 2 ? 5 : 3;
      var groupAdded = 0;
      var values = (filter.values || []).slice().sort(function (a, b) {
        return Number(b.count || 0) - Number(a.count || 0);
      });

      for (var v = 0; v < values.length && quickValues.length < valueLimit; v++) {
        if (groupAdded >= groupLimit) break;
        if (Number(values[v].count || 0) <= 0 && !state.selectedValues[values[v].id]) continue;
        if (!isUsefulQuickLabel(values[v].label)) continue;

        if (addQuickFilterValue(
          quickValues,
          values[v],
          values[v].label,
          used,
          usedLabels,
          valueLimit
        )) {
          groupAdded++;
        }
      }
    }

    quickValues = preserveQuickFilterOrder(quickValues);

    var html = '';
    for (var q = 0; q < quickValues.length; q++) {
      var quick = quickValues[q];
      html +=
        '<button type="button" class="pw-quick-chip' +
          (state.selectedValues[quick.value.id] ? ' is-active' : '') + '"' +
          ' data-pw-filter-id="' + escapeAttr(quick.value.id) + '"' +
          ' data-pw-filter-input="' + escapeAttr(JSON.stringify(normalizeFilterInput(quick.value.input))) + '"' +
          ' data-pw-filter-label="' + escapeAttr(quick.label) + '">' +
          escapeHtml(quick.label) +
        '</button>';
    }

    html +=
      '<button type="button" class="pw-quick-chip' +
        (state.priceMax === '10' ? ' is-active' : '') + '" data-pw-price-max="10">Under £10</button>' +
      '<button type="button" class="pw-quick-chip' +
        (state.priceMax === '20' ? ' is-active' : '') + '" data-pw-price-max="20">Under £20</button>';

    target.innerHTML = html;
  }

  function preserveQuickFilterOrder(values) {
    if (!state.quickFilterOrder.length) {
      state.quickFilterOrder = values.map(function (item) {
        return item.value.id;
      });
      return values;
    }

    var positions = {};
    for (var i = 0; i < state.quickFilterOrder.length; i++) {
      positions[state.quickFilterOrder[i]] = i;
    }

    values = values.map(function (item, index) {
      return { item: item, generatedIndex: index };
    }).sort(function (a, b) {
      var aKnown = Object.prototype.hasOwnProperty.call(positions, a.item.value.id);
      var bKnown = Object.prototype.hasOwnProperty.call(positions, b.item.value.id);

      if (aKnown && bKnown) return positions[a.item.value.id] - positions[b.item.value.id];
      if (aKnown) return -1;
      if (bKnown) return 1;
      return a.generatedIndex - b.generatedIndex;
    }).map(function (entry) {
      return entry.item;
    });

    for (var v = 0; v < values.length; v++) {
      if (!Object.prototype.hasOwnProperty.call(positions, values[v].value.id)) {
        state.quickFilterOrder.push(values[v].value.id);
      }
    }

    return values;
  }

  function addQuickFilterValue(target, value, label, used, usedLabels, limit) {
    if (!value) return false;

    var cleanLabel = String(label || value.label || '').trim();
    var labelKey = cleanLabel.toLowerCase();

    if (!value || !value.id || target.length >= limit || used[value.id] || usedLabels[labelKey]) {
      return false;
    }

    used[value.id] = true;
    usedLabels[labelKey] = true;
    target.push({ value: value, label: cleanLabel });
    return true;
  }

  function quickFilterPriority(filter) {
    var label = String(filter.label || '').toLowerCase();

    if (label.indexOf('type') !== -1 || label.indexOf('category') !== -1) return 0;
    if (label.indexOf('style') !== -1 || label.indexOf('feature') !== -1) return 1;
    if (label.indexOf('brand') !== -1 || label.indexOf('vendor') !== -1) return 2;
    if (label.indexOf('material') !== -1 || label.indexOf('fabric') !== -1) return 3;
    if (label.indexOf('vintage') !== -1 || label.indexOf('condition') !== -1) return 4;
    return 10;
  }

  function isUsefulQuickLabel(label) {
    var clean = String(label || '').trim();
    return clean.length > 1 && clean.length <= 24 && !/^all\b/i.test(clean);
  }

  function findQuickFilterValue(filters, terms, used) {
    for (var t = 0; t < terms.length; t++) {
      var needle = String(terms[t]).toLowerCase();

      for (var f = 0; f < filters.length; f++) {
        var values = filters[f].values || [];

        for (var v = 0; v < values.length; v++) {
          var value = values[v];
          var label = String(value.label || '').toLowerCase();

          if (!used[value.id] && value.count > 0 && label.indexOf(needle) !== -1) {
            return value;
          }
        }
      }
    }

    return null;
  }

  function renderActiveFilters() {
    var target = document.getElementById('PrewornActiveFilters');
    var count = document.getElementById('PrewornActiveFilterCount');
    if (!target) return;
    renderSearchHeading();

    var keys = Object.keys(state.selectedValues);
    var hasCollectionQuery = state.mode === 'collection' && state.query &&
      (state.usingCatalog || state.collectionKeywordSearch);
    var activeCount = keys.length + ((state.priceMin || state.priceMax) ? 1 : 0) +
      (hasCollectionQuery ? 1 : 0);
    var html = '';

    if (hasCollectionQuery) {
      html +=
        '<button type="button" class="pw-active-pill" data-pw-remove-collection-query>' +
          escapeHtml(state.query) + ' <span aria-hidden="true">×</span>' +
        '</button>';
    }

    for (var i = 0; i < keys.length; i++) {
      var selected = state.selectedValues[keys[i]];
      html +=
        '<button type="button" class="pw-active-pill" data-pw-remove-filter="' +
          escapeAttr(keys[i]) + '">' +
          escapeHtml(selected.label || 'Filter') + ' <span aria-hidden="true">×</span>' +
        '</button>';
    }

    if (state.priceMin || state.priceMax) {
      var priceLabel = state.priceMin && state.priceMax
        ? '£' + state.priceMin + '–£' + state.priceMax
        : state.priceMin
          ? 'Over £' + state.priceMin
          : 'Under £' + state.priceMax;

      html +=
        '<button type="button" class="pw-active-pill" data-pw-remove-price>' +
          escapeHtml(priceLabel) + ' <span aria-hidden="true">×</span>' +
        '</button>';
    }

    target.innerHTML = html;
    target.hidden = activeCount === 0;
    if (count) count.textContent = String(activeCount);

    var removeButtons = target.querySelectorAll('[data-pw-remove-filter]');
    for (var r = 0; r < removeButtons.length; r++) {
      removeButtons[r].addEventListener('click', function () {
        var filterId = this.getAttribute('data-pw-remove-filter');
        delete state.selectedValues[filterId];

        if (filterId === state.collectionQueryFilterId) {
          clearCollectionQuery();
        }

        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var removeCollectionQuery = target.querySelector('[data-pw-remove-collection-query]');
    if (removeCollectionQuery) {
      removeCollectionQuery.addEventListener('click', function () {
        clearCollectionQuery();
        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var removePrice = target.querySelector('[data-pw-remove-price]');
    if (removePrice) {
      removePrice.addEventListener('click', function () {
        state.priceMin = '';
        state.priceMax = '';
        resetPagination();
        navigateWithHistory(1, null);
      });
    }
  }
  function renderFilters(filters) {
    var body = document.getElementById('PrewornSearchFiltersBody');
    if (!body) return;

    if (!filters.length) {
      body.innerHTML = state.catalogFacetsDeferred
        ? '<p class="pw-search__muted">Loading filters…</p>'
        : '<p class="pw-search__muted">No filters available.</p>';
      bindFilterEvents();
      return;
    }

    filters = filters.slice().sort(function (a, b) {
      return filterPriority(a) - filterPriority(b);
    });

    var html = '';

    for (var i = 0; i < filters.length; i++) {
      var filter = filters[i];

      if (filter.type === 'PRICE_RANGE') {
        html += renderPriceFilter(filter);
        continue;
      }

      if (filter.type !== 'LIST' && filter.type !== 'BOOLEAN') {
        continue;
      }

      html += renderListFilter(filter);
    }

    body.innerHTML = html;
    bindFilterEvents();
  }

  function filterGroupKey(filter) {
    return String(filter.id || filter.label || 'filter')
      .toLowerCase()
      .replace(/[^a-z0-9:_-]+/g, '-');
  }

  function filterGroupIsOpen(filter, defaultOpen) {
    var key = filterGroupKey(filter);
    if (Object.prototype.hasOwnProperty.call(state.openFilterGroups, key)) {
      return state.openFilterGroups[key];
    }

    var values = filter.values || [];
    for (var i = 0; i < values.length; i++) {
      if (state.selectedValues[values[i].id]) return true;
    }

    return !!defaultOpen;
  }

  function filterPriority(filter) {
    var label = String(filter.label || '').toLowerCase();

    if (label.indexOf('department') !== -1) return 0;
    if (label === 'type' || label.indexOf('product type') !== -1) return 1;
    if (label.indexOf('style') !== -1) return 2;
    if (label.indexOf('size') !== -1) return 3;
    if (label.indexOf('brand') !== -1 || label.indexOf('vendor') !== -1) return 4;
    if (label.indexOf('colour') !== -1 || label.indexOf('color') !== -1) return 5;
    if (label.indexOf('category') !== -1) return 6;
    if (label.indexOf('material') !== -1) return 7;
    if (label.indexOf('pattern') !== -1) return 8;
    if (label.indexOf('condition') !== -1) return 98;
    if (filter.type === 'PRICE_RANGE' || label.indexOf('price') !== -1) return 99;
    return 50;
  }

  function renderListFilter(filter) {
    var labelLower = String(filter.label || '').toLowerCase();
    var isDepartment = labelLower.indexOf('department') !== -1;
    var isSize = labelLower === 'size' || labelLower.indexOf('size') !== -1;
    var isProminent = filterPriority(filter) <= 4;
    var isColour =
      labelLower === 'colour' ||
      labelLower === 'color' ||
      labelLower.indexOf('colour') !== -1 ||
      labelLower.indexOf('color') !== -1;

    var values = (filter.values || []).slice();
    var groupKey = filterGroupKey(filter);

    // Both the catalogue API and Shopify fallback can supply these values.
    // Enforce popularity order in the browser so fallback responses cannot
    // silently change Brand, Style and other lists back to alphabetical.
    if (!isDepartment && !isSize) {
      values.sort(compareFacetValueCounts);
    }

    if (isDepartment) {
      var departmentOrder = {
        men: 0,
        women: 1,
        boys: 2,
        girls: 3,
        baby: 4
      };

      values = values.map(function (value) {
        return Object.assign({}, value, {
          label: departmentDisplayLabel(value.label)
        });
      }).sort(function (a, b) {
        var aKey = departmentKey(a.label);
        var bKey = departmentKey(b.label);
        var aOrder = Object.prototype.hasOwnProperty.call(departmentOrder, aKey)
          ? departmentOrder[aKey] : 99;
        var bOrder = Object.prototype.hasOwnProperty.call(departmentOrder, bKey)
          ? departmentOrder[bKey] : 99;
        return aOrder - bOrder;
      });
    }

    if (isSize) {
      values = values.map(function (value) {
        return Object.assign({}, value, {
          label: sizeDisplayLabel(value.label)
        });
      }).sort(compareSizeValues);
    }

    var html =
      '<details class="pw-filter" data-pw-filter-group="' +
        escapeAttr(groupKey) + '" ' +
        (filterGroupIsOpen(filter, isProminent) ? 'open' : '') + '>' +
        '<summary>' +
          '<span>' + escapeHtml(filter.label) + '</span>' +
          '<span class="pw-filter__chevron">⌄</span>' +
        '</summary>' +
        '<div class="pw-filter__body">';

    if (isSize) {
      html += '<div class="pw-filter__sizes">';

      for (var i = 0; i < values.length; i++) {
        html += renderSizeValue(values[i]);
      }

      html += '</div>';

    } else if (isColour) {
      html += '<div class="pw-filter__colours">';

      for (var c = 0; c < values.length; c++) {
        html += renderColourValue(values[c]);
      }

      html += '</div>';

    } else {
      html += '<div class="pw-filter__list">';

      var visibleLimit = state.expandedFilterGroups[groupKey]
        ? values.length
        : 20;

      for (var j = 0; j < values.length; j++) {
        var hidden = j >= visibleLimit ? ' pw-filter-value--extra' : '';
        html += renderGenericValue(values[j], hidden);
      }

      if (values.length > visibleLimit) {
        html +=
          '<button type="button" class="pw-filter__more" data-pw-show-more data-pw-filter-group-key="' +
            escapeAttr(groupKey) + '">' +
            'Show more (' + (values.length - visibleLimit) + ')' +
          '</button>';
      }

      html += '</div>';
    }

    html += '</div></details>';

    return html;
  }

  function compareFacetValueCounts(a, b) {
    var countDifference = Number(b.count || 0) - Number(a.count || 0);
    if (countDifference !== 0) return countDifference;

    return String(a.label || '').localeCompare(
      String(b.label || ''),
      'en-GB',
      { sensitivity: 'base' }
    );
  }

  function departmentKey(label) {
    var clean = String(label || '')
      .toLowerCase()
      .replace(/^department[\s:_-]*/i, '')
      .replace(/[^a-z]/g, '');

    if (clean === 'men' || clean === 'mens' || clean === 'male') return 'men';
    if (clean === 'women' || clean === 'womens' || clean === 'female') return 'women';
    if (clean === 'boys' || clean === 'boy') return 'boys';
    if (clean === 'girls' || clean === 'girl') return 'girls';
    if (clean.indexOf('baby') === 0 || clean === 'infant') return 'baby';
    return 'other';
  }

  function departmentDisplayLabel(label) {
    var key = departmentKey(label);
    var displayLabels = {
      men: 'Men',
      women: 'Women',
      boys: 'Boys',
      girls: 'Girls',
      baby: 'Baby'
    };
    return displayLabels[key] || String(label || '')
      .replace(/^department[\s:_-]*/i, '')
      .trim();
  }

  function sizeDisplayLabel(label) {
    var clean = String(label || '')
      .replace(/^(?:uk\s+shoe\s+size|shoe\s+size|clothing\s+size|garment\s+size|size)[\s:_-]*/i, '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    var compact = clean.toLowerCase().replace(/[^a-z0-9]+/g, '');
    var named = {
      xxsmall: 'XXS',
      extrasmall: 'XS',
      xsmall: 'XS',
      small: 'S',
      medium: 'M',
      large: 'L',
      xlarge: 'XL',
      extralarge: 'XL',
      xxlarge: '2XL',
      xxxlarge: '3XL',
      onesize: 'One Size'
    };

    if (named[compact]) return named[compact];
    if (/^(xxs|xs|s|m|l|xl|xxl|xxxl|[1-9]xl)$/i.test(clean)) {
      var upper = clean.toUpperCase();
      if (upper === 'XXL') return '2XL';
      if (upper === 'XXXL') return '3XL';
      return upper;
    }

    var inches = clean.match(/^([0-9]+(?:\.[0-9]+)?)\s*(?:in|inch|inches|")$/i);
    if (inches) return inches[1] + ' in';
    var uk = clean.match(/^uk\s*([0-9]+(?:\.[05])?)$/i);
    if (uk) return 'UK ' + uk[1];
    return clean;
  }

  function compareSizeValues(a, b) {
    var aKey = sizeSortKey(a.label);
    var bKey = sizeSortKey(b.label);
    return aKey < bKey ? -1 : (aKey > bKey ? 1 : 0);
  }

  function sizeSortKey(label) {
    var value = sizeDisplayLabel(label);
    var upper = value.toUpperCase();
    var standard = {
      XXS: 0,
      XS: 1,
      S: 2,
      M: 3,
      L: 4,
      XL: 5,
      '2XL': 6,
      '3XL': 7,
      '4XL': 8,
      '5XL': 9,
      '6XL': 10,
      'ONE SIZE': 20
    };

    if (Object.prototype.hasOwnProperty.call(standard, upper)) {
      return '0:' + String(standard[upper]).padStart(3, '0');
    }

    var plainNumber = value.match(/^([0-9]+(?:\.[0-9]+)?)(?:\s*\/\s*([0-9]+))?$/);
    if (plainNumber) {
      return '1:' + String(parseFloat(plainNumber[1]) * 10).padStart(5, '0') + ':' + value;
    }

    var uk = value.match(/^UK\s*([0-9]+(?:\.[05])?)$/i);
    if (uk) {
      return '2:' + String(parseFloat(uk[1]) * 10).padStart(5, '0');
    }

    var inches = value.match(/^([0-9]+(?:\.[0-9]+)?)\s*in$/i);
    if (inches) {
      return '3:' + String(parseFloat(inches[1]) * 10).padStart(5, '0');
    }

    return '9:' + upper;
  }

  function renderSizeValue(value) {
    var active = !!state.selectedValues[value.id];

    return (
      '<button type="button"' +
        ' class="pw-size' + (active ? ' is-active' : '') +
          (value.count === 0 && !active ? ' is-disabled' : '') + '"' +
        ' data-pw-filter-id="' + escapeAttr(value.id) + '"' +
        ' data-pw-filter-input="' + escapeAttr(JSON.stringify(normalizeFilterInput(value.input))) + '"' +
        ' data-pw-filter-label="' + escapeAttr(value.label) + '"' +
        (value.count === 0 && !active ? ' disabled' : '') +
        ' title="' + escapeAttr(value.label + ' (' + value.count + ')') + '">' +
        escapeHtml(value.label) +
      '</button>'
    );
  }

  function renderColourValue(value) {
    var active = !!state.selectedValues[value.id];
    var swatch = getSwatch(value);

    return (
      '<button type="button"' +
        ' class="pw-colour' + (active ? ' is-active' : '') +
          (value.count === 0 && !active ? ' is-disabled' : '') + '"' +
        ' data-pw-filter-id="' + escapeAttr(value.id) + '"' +
        ' data-pw-filter-input="' + escapeAttr(JSON.stringify(normalizeFilterInput(value.input))) + '"' +
        ' data-pw-filter-label="' + escapeAttr(value.label) + '"' +
        (value.count === 0 && !active ? ' disabled' : '') +
        ' title="' + escapeAttr(value.label + ' (' + value.count + ')') + '">' +
        '<span class="pw-colour__dot" style="' + escapeAttr(swatch) + '"></span>' +
        '<span class="pw-visually-hidden">' + escapeHtml(value.label) + '</span>' +
      '</button>'
    );
  }

  function renderGenericValue(value, extraClass) {
    var active = !!state.selectedValues[value.id];
    var disabled = value.count === 0 && !active;

    return (
      '<button type="button"' +
        ' class="pw-filter-value' + (active ? ' is-active' : '') +
          (disabled ? ' is-disabled' : '') + (extraClass || '') + '"' +
        ' data-pw-filter-id="' + escapeAttr(value.id) + '"' +
        ' data-pw-filter-input="' + escapeAttr(JSON.stringify(normalizeFilterInput(value.input))) + '"' +
        ' data-pw-filter-label="' + escapeAttr(value.label) + '"' +
        (disabled ? ' disabled' : '') + '>' +
        '<span class="pw-filter-value__box"></span>' +
        '<span class="pw-filter-value__label">' + escapeHtml(value.label) + '</span>' +
        '<span class="pw-filter-value__count">' + Number(value.count || 0).toLocaleString('en-GB') + '</span>' +
      '</button>'
    );
  }

  function renderPriceFilter(filter) {
    var groupKey = filterGroupKey(filter);
    return (
      '<details class="pw-filter" data-pw-filter-group="' +
        escapeAttr(groupKey) + '" ' +
        (filterGroupIsOpen(filter, false) ? 'open' : '') + '>' +
        '<summary>' +
          '<span>' + escapeHtml(filter.label || 'Price') + '</span>' +
          '<span class="pw-filter__chevron">⌄</span>' +
        '</summary>' +
        '<div class="pw-filter__body">' +
          '<div class="pw-price">' +
            '<label>From £<input id="PrewornPriceMin" type="number" min="0" step="0.01" value="' +
              escapeAttr(state.priceMin) + '"></label>' +
            '<label>To £<input id="PrewornPriceMax" type="number" min="0" step="0.01" value="' +
              escapeAttr(state.priceMax) + '"></label>' +
            '<button type="button" class="pw-price__apply" data-pw-price-apply>Apply</button>' +
          '</div>' +
        '</div>' +
      '</details>'
    );
  }

  function bindFilterEvents() {
    var filterGroups = app.querySelectorAll('[data-pw-filter-group]');
    for (var g = 0; g < filterGroups.length; g++) {
      filterGroups[g].addEventListener('toggle', function () {
        var key = this.getAttribute('data-pw-filter-group');
        if (key) state.openFilterGroups[key] = this.open;
      });
    }

    var values = app.querySelectorAll('[data-pw-filter-id]');

    for (var i = 0; i < values.length; i++) {
      values[i].addEventListener('click', function () {
        var id = this.getAttribute('data-pw-filter-id');
        var raw = this.getAttribute('data-pw-filter-input');
        var label = this.getAttribute('data-pw-filter-label');

        if (state.selectedValues[id]) {
          delete state.selectedValues[id];
        } else {
          try {
            state.selectedValues[id] = {
              input: JSON.parse(raw),
              label: label
            };
          } catch (e) {
            console.error('[Preworn Browse] Invalid filter input', e, raw);
            return;
          }
        }

        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var priceButton = app.querySelector('[data-pw-price-apply]');
    if (priceButton) {
      priceButton.addEventListener('click', function () {
        var min = document.getElementById('PrewornPriceMin');
        var max = document.getElementById('PrewornPriceMax');

        state.priceMin = min ? min.value.trim() : '';
        state.priceMax = max ? max.value.trim() : '';

        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var quickPrices = app.querySelectorAll('[data-pw-price-max]');
    for (var q = 0; q < quickPrices.length; q++) {
      quickPrices[q].addEventListener('click', function () {
        var maximum = this.getAttribute('data-pw-price-max') || '';
        state.priceMin = '';
        state.priceMax = state.priceMax === maximum ? '' : maximum;
        resetPagination();
        navigateWithHistory(1, null);
      });
    }

    var moreButtons = app.querySelectorAll('[data-pw-show-more]');

    for (var m = 0; m < moreButtons.length; m++) {
      moreButtons[m].addEventListener('click', function () {
        var groupKey = this.getAttribute('data-pw-filter-group-key');
        if (groupKey) state.expandedFilterGroups[groupKey] = true;
        var parent = this.parentNode;
        var extras = parent.querySelectorAll('.pw-filter-value--extra');

        for (var x = 0; x < extras.length; x++) {
          extras[x].classList.remove('pw-filter-value--extra');
        }

        this.remove();
      });
    }
  }

  function paginationSequence(currentPage, totalPages) {
    var pages = [];
    var start;
    var end;
    var page;

    if (totalPages <= 7) {
      for (page = 1; page <= totalPages; page++) pages.push(page);
      return pages;
    }

    start = Math.max(2, currentPage - 2);
    end = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 4) {
      start = 2;
      end = 5;
    } else if (currentPage >= totalPages - 3) {
      start = totalPages - 4;
      end = totalPages - 1;
    }

    pages.push(1);
    if (start > 2) pages.push('ellipsis-before');
    for (page = start; page <= end; page++) pages.push(page);
    if (end < totalPages - 1) pages.push('ellipsis-after');
    pages.push(totalPages);

    return pages;
  }

  function renderPagination(result) {
    var nav = document.getElementById('PrewornSearchPagination');
    if (!nav) return;

    var hasPrevious = state.currentPage > 1;
    var hasNext = !!(result.pageInfo && result.pageInfo.hasNextPage);
    var hasKnownTotal = true;
    var totalPages;
    var status;

    if (result.catalogSource) {
      totalPages = Math.max(
        1,
        Number(result.totalPages || 0) ||
          Math.ceil(Number(result.totalCount || 0) / CONFIG.pageSize)
      );

      hasNext = hasNext && state.currentPage < totalPages;
      status = 'Page <strong>' + state.currentPage +
        '</strong> of <strong>' + totalPages.toLocaleString('en-GB') +
        '</strong>';
    } else if (state.mode === 'collection' && !state.collectionKeywordSearch) {
      hasKnownTotal = false;
      totalPages = state.currentPage + (hasNext ? 1 : 0);
      status = 'Page <strong>' + state.currentPage + '</strong>';
    } else {
      var total = Math.min(
        Number(result.totalCount || 0),
        CONFIG.shopifyFallbackMaxResults
      );
      totalPages = Math.max(1, Math.ceil(total / CONFIG.pageSize));

      hasNext = hasNext && state.currentPage < totalPages;
      status = 'Page <strong>' + state.currentPage +
        '</strong> of <strong>' + totalPages + '</strong>';
    }

    var numbers = hasKnownTotal
      ? paginationSequence(state.currentPage, totalPages)
      : [state.currentPage];
    var numberMarkup = '';

    for (var i = 0; i < numbers.length; i++) {
      var pageNumber = numbers[i];

      if (typeof pageNumber !== 'number') {
        numberMarkup +=
          '<span class="pw-page-ellipsis" aria-hidden="true">•••</span>';
        continue;
      }

      var isCurrent = pageNumber === state.currentPage;
      var isNear = Math.abs(pageNumber - state.currentPage) <= 1;
      var canNavigate = result.catalogSource || pageNumber === 1 ||
        Object.prototype.hasOwnProperty.call(state.pageCursors, pageNumber);
      var pageClass = 'pw-page-number' +
        (isCurrent ? ' is-current' : '') +
        (isNear ? ' pw-page-number--near' : '');

      if (isCurrent) {
        numberMarkup += '<span class="' + pageClass +
          '" aria-current="page"><span class="pw-visually-hidden">Page </span>' +
          pageNumber + '</span>';
      } else if (canNavigate) {
        numberMarkup += '<button type="button" class="' + pageClass +
          '" data-pw-page="' + pageNumber + '" aria-label="Go to page ' +
          pageNumber + '">' + pageNumber + '</button>';
      } else {
        numberMarkup += '<span class="' + pageClass +
          ' is-disabled" aria-disabled="true">' + pageNumber + '</span>';
      }
    }

    nav.innerHTML =
      '<div class="pw-page-shell">' +
        '<span class="pw-page-kicker">Browse results</span>' +
        '<div class="pw-page-controls">' +
          '<button type="button" class="pw-page-button pw-page-button--previous" data-pw-prev ' +
            (hasPrevious ? '' : 'disabled') +
            '><span aria-hidden="true">←</span><span class="pw-page-button__label">Previous</span></button>' +
          '<div class="pw-page-numbers" aria-label="Choose a results page">' +
            numberMarkup +
          '</div>' +
          '<button type="button" class="pw-page-button pw-page-button--next" data-pw-next ' +
            (hasNext ? '' : 'disabled') +
            '><span class="pw-page-button__label">Next</span><span aria-hidden="true">→</span></button>' +
        '</div>' +
        '<span class="pw-page-status" aria-live="polite">' + status + '</span>' +
      '</div>';

    var prev = nav.querySelector('[data-pw-prev]');
    var next = nav.querySelector('[data-pw-next]');

    if (prev && hasPrevious) {
      prev.addEventListener('click', function () {
        var targetPage = state.currentPage - 1;
        var cursor = state.pageCursors[targetPage] || null;
        navigateWithHistory(targetPage, cursor);
      });
    }

    if (next && hasNext) {
      next.addEventListener('click', function () {
        var targetPage = state.currentPage + 1;
        var cursor = state.pageCursors[targetPage];

        if (!cursor && result.pageInfo) {
          cursor = result.pageInfo.endCursor;
          state.pageCursors[targetPage] = cursor;
        }

        navigateWithHistory(targetPage, cursor);
      });
    }

    var pageButtons = nav.querySelectorAll('[data-pw-page]');
    for (var p = 0; p < pageButtons.length; p++) {
      pageButtons[p].addEventListener('click', function () {
        var targetPage = Number(this.getAttribute('data-pw-page'));
        if (!targetPage || targetPage === state.currentPage) return;
        navigateWithHistory(targetPage, state.pageCursors[targetPage] || null);
      });
    }
  }

  function normalizeFilterInput(input) {
    if (input === null || typeof input === 'undefined') {
      return {};
    }

    if (typeof input === 'string') {
      try {
        return JSON.parse(input);
      } catch (e) {
        return {};
      }
    }

    return input;
  }

  function getSwatch(value) {
    if (value.swatch) {
      if (value.swatch.color) {
        return 'background:' + value.swatch.color + ';';
      }

      if (value.swatch.image &&
          value.swatch.image.image &&
          value.swatch.image.image.url) {
        return 'background-image:url("' +
          cssUrl(value.swatch.image.image.url) +
          '");background-size:cover;background-position:center;';
      }
    }

    return 'background:' + fallbackColour(value.label) + ';';
  }

  function fallbackColour(label) {
    var name = String(label || '').toLowerCase().trim();

    var map = {
      black: '#111111',
      white: '#ffffff',
      grey: '#999999',
      gray: '#999999',
      blue: '#2455d6',
      navy: '#182644',
      red: '#d92f2f',
      pink: '#ef9db8',
      green: '#3b8b42',
      purple: '#7b4b9e',
      yellow: '#f2df31',
      orange: '#e98b2a',
      brown: '#86563b',
      beige: '#dfcfac',
      cream: '#f1ead8',
      gold: '#c7a441',
      silver: '#c8c8c8',
      khaki: '#8c8a59',
      burgundy: '#6d1f2b',
      maroon: '#6d1f2b',
      turquoise: '#39a7a7',
      teal: '#287d7a'
    };

    if (map[name]) return map[name];

    if (
      name.indexOf('multi') !== -1 ||
      name.indexOf('mixed') !== -1
    ) {
      return 'conic-gradient(#e53935,#fb8c00,#fdd835,#43a047,#1e88e5,#8e24aa,#e53935)';
    }

    return '#d9d9d9';
  }

  function hasActiveFilters() {
    return Object.keys(state.selectedValues).length > 0 ||
      state.priceMin !== '' ||
      state.priceMax !== '';
  }

  function hideNativeBrowse() {
    var nativeGrid = document.getElementById('ProductGridContainer');

    if (nativeGrid && nativeGrid.parentElement) {
      nativeGrid.parentElement.style.display = 'none';
    }

    var nativeSort = document.querySelector('.facets-vertical-sort');
    if (nativeSort) nativeSort.style.display = 'none';

    var nativeSearchFilters = document.getElementById('main-search-filters');
    if (nativeSearchFilters) nativeSearchFilters.style.display = 'none';

    var nativeCollectionFilters = document.getElementById('main-collection-filters');
    if (nativeCollectionFilters) nativeCollectionFilters.style.display = 'none';

    app.style.display = 'block';
  }

  function showNativeFallback() {
    var bootStyles = document.getElementById('PrewornWideSearchBootStyles');
    if (bootStyles) bootStyles.remove();

    var nativeGrid = document.getElementById('ProductGridContainer');
    if (nativeGrid) nativeGrid.style.display = '';

    if (nativeGrid && nativeGrid.parentElement) {
      nativeGrid.parentElement.style.display = '';
    }

    var nativeSort = document.querySelector('.facets-vertical-sort');
    if (nativeSort) nativeSort.style.display = '';

    var nativeSearchFilters = document.getElementById('main-search-filters');
    if (nativeSearchFilters) nativeSearchFilters.style.display = '';

    var nativeCollectionFilters = document.getElementById('main-collection-filters');
    if (nativeCollectionFilters) nativeCollectionFilters.style.display = '';
  }

  function setLoading(show) {
    var loading = document.getElementById('PrewornSearchLoading');

    if (loading) {
      loading.hidden = !show;
    }

    if (app) {
      app.classList.toggle('is-loading', !!show);
      app.setAttribute('aria-busy', show ? 'true' : 'false');
    }
  }

  function showError(message) {
    var error = document.getElementById('PrewornSearchError');
    if (!error) return;

    error.textContent = message;
    error.hidden = false;
  }

  function clearError() {
    var error = document.getElementById('PrewornSearchError');
    if (!error) return;

    error.textContent = '';
    error.hidden = true;
  }

  function renderFatal(message) {
    app.innerHTML =
      '<div style="padding:16px;border:1px solid #c00;color:#900;background:#fff;">' +
        escapeHtml(message) +
      '</div>';
  }

  function scrollResultsIntoView(pageNumber) {
    if (pageNumber <= 1) return;

    var rect = app.getBoundingClientRect();
    var target = window.pageYOffset + rect.top - 20;

    window.scrollTo({
      top: Math.max(0, target),
      behavior: 'smooth'
    });
  }

  function money(amount, currency) {
    var value = Number(amount);

    if (isNaN(value)) return '';

    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(value);
    } catch (e) {
      return '£' + value.toFixed(2);
    }
  }

  function escapeHtml(value) {
    return String(value === null || typeof value === 'undefined' ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function escapeAttr(value) {
    return escapeHtml(value);
  }

  function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function cssUrl(value) {
    return String(value || '')
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
  }

  function injectStyles() {
    if (document.getElementById('PrewornSearchStyles')) return;

    var style = document.createElement('style');
    style.id = 'PrewornSearchStyles';

style.textContent = [
 '#PrewornWideSearch{display:block;width:100%;max-width:none!important;min-height:100vh;background:#fff;}',

  '.pw-search{display:grid;grid-template-columns:270px minmax(0,1fr);gap:26px;width:100%;padding:0 28px 36px;box-sizing:border-box;background:#fff;}',

  '.pw-search__filters{position:sticky;top:130px;align-self:start;max-height:calc(100vh - 150px);overflow:auto;scrollbar-width:thin;border-right:1px solid #e5e5e5;padding-right:24px;min-width:0;}',

  '.pw-search__results{min-width:0;padding-top:30px;}',

  '.pw-search__filter-head{display:flex;align-items:center;justify-content:space-between;padding:6px 0 14px;border-bottom:1px solid #e5e5e5;}',

  '.pw-search__filter-head strong{letter-spacing:.01em;font-size:15px;color:#14231e;}',

  '.pw-search__filter-actions{display:flex;align-items:center;gap:12px;}',

  '.pw-search__clear{appearance:none;border:0;background:none;text-decoration:underline;cursor:pointer;font:inherit;font-size:12px;padding:0;}',

  '.pw-search__filter-close{display:none;appearance:none;width:38px;height:38px;align-items:center;justify-content:center;padding:0;border:1px solid #cfd4d0;border-radius:50%;background:#fff;color:#14231e;cursor:pointer;font:inherit;font-size:27px;line-height:1;}',

  '.pw-search__intro{margin:0 0 16px;padding:0 0 17px;border-bottom:1px solid #e7e7e3;}',

  '.pw-search__query{margin:0 0 7px;font-size:clamp(24px,2vw,28px);font-weight:700;line-height:1.08;letter-spacing:-.025em;color:#14231e;}',

  '.pw-search__query-context{display:inline-flex;flex-wrap:wrap;align-items:center;gap:5px 8px;margin-left:10px;color:#56645e;font-size:clamp(11px,.82vw,13px);font-weight:500;letter-spacing:.01em;line-height:1.35;vertical-align:.16em;}',

  '.pw-search__query-context::before{content:"—";color:#8a938f;font-weight:500;}',

  '.pw-search__query-context>span{white-space:nowrap;}',

  '.pw-search__query-context b{color:#315d3b;font-size:.82em;font-weight:850;letter-spacing:.08em;text-transform:uppercase;}',

  '.pw-search__query-context>i{color:#a4aca8;font-style:normal;}',

  '.pw-search__similar-kicker{display:block;margin-bottom:8px;color:#4e5d56;font-size:10px;font-weight:850;letter-spacing:.14em;line-height:1;text-transform:uppercase;}',

  '.pw-search__query .pw-search__similar-kicker + strong{display:block;font-size:clamp(25px,2.4vw,36px);}',

  '.pw-search__count{margin:0;font-size:14px;color:#616862;}',

  '.pw-quick-filters{display:flex;flex-wrap:wrap;gap:8px;margin:0 0 16px;}',

  '.pw-quick-chip{appearance:none;height:36px;padding:0 15px;border:1px solid #cfd4d0;border-radius:999px;background:#fff;color:#26332d;cursor:pointer;font:inherit;font-size:13px;white-space:nowrap;transition:border-color .18s ease,background .18s ease,color .18s ease;}',

  '.pw-quick-chip:hover{border-color:#315d3b;color:#1a401a;}',

  '.pw-quick-chip.is-active{border-color:#315d3b;background:#315d3b;color:#fff;}',

  '.pw-active-filters{display:flex;flex-wrap:wrap;gap:7px;margin:0 0 14px;}',

  '.pw-active-filters[hidden]{display:none!important;}',

  '.pw-active-pill{appearance:none;display:inline-flex;align-items:center;gap:7px;min-height:31px;padding:5px 10px;border:0;border-radius:999px;background:#edf3ee;color:#1f4a2a;cursor:pointer;font:inherit;font-size:12px;}',

  '.pw-active-pill span{font-size:16px;line-height:1;}',

  '.pw-search__toolbar{display:flex;justify-content:space-between;align-items:center;gap:20px;margin-bottom:18px;padding:11px 0;border-top:1px solid #e7e7e3;border-bottom:1px solid #e7e7e3;}',

  '.pw-search__toolbar-summary{display:flex;align-items:center;gap:12px;font-size:13px;color:#2f3733;}',

  '.pw-search__filter-toggle{display:none;appearance:none;height:36px;padding:0 13px;border:1px solid #cfd4d0;border-radius:4px;background:#fff;color:#14231e;font:inherit;font-size:13px;cursor:pointer;}',

  '.pw-search__sort{display:flex;align-items:center;gap:9px;font-size:13px;white-space:nowrap;}',

  '.pw-search__sort select{min-width:175px;height:38px;border:1px solid #cfd4d0;border-radius:3px;background:#fff;color:#202824;padding:0 32px 0 10px;}',


  /* PRODUCT GRID */

  '.pw-search__grid{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:18px 20px;align-items:start;}',

  '.pw-card{min-width:0;align-self:start;background:#fff;border:0;border-radius:8px;overflow:visible;display:flex;flex-direction:column;height:auto;box-sizing:border-box;}',

  '.pw-card__link{display:flex;flex-direction:column;width:100%;height:auto;color:inherit;text-decoration:none;background:#fff;}',

  '.pw-card__image-link{display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:inherit;text-decoration:none;}',

  /* DISCOUNT DISPLAY */
'.pw-card__discount{position:absolute;right:11px;top:11px;width:54px;height:54px;border-radius:50%;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#315d3b;color:#fff;text-align:center;z-index:3;line-height:1;box-shadow:0 1px 4px rgba(0,0,0,.12);transition:transform .18s ease;}',
'.pw-card:hover .pw-card__discount{transform:rotate(12deg) scale(1.3);}',
'.pw-card__discount span{display:block;font-size:16px;font-weight:700;line-height:17px;}',

'.pw-card__discount small{display:block;font-size:9px;font-weight:700;letter-spacing:.05em;margin-top:2px;}',

  '.pw-discount--10{background:#a6a6a6;}',

  '.pw-discount--20{background:#65d669;}',

  '.pw-discount--30{background:#65b0d6;}',

  '.pw-discount--40{background:#9f65d6;}',

  '.pw-discount--45{background:#d68f65;}',

  '.pw-discount--55{background:#d66565;}',

  '.pw-card__sold-badge{background:#c8ff3d;color:#08271f;}',

  '.pw-card__sold-badge span{font-size:11px;letter-spacing:.08em;}',

  '.pw-card__new-badge{position:absolute;top:11px;left:11px;z-index:5;display:inline-flex;min-height:21px;align-items:center;justify-content:center;padding:0 9px;color:#08271f;background:#c8ff3d;border-radius:999px;font-size:8px;font-weight:850;letter-spacing:.05em;line-height:1;box-shadow:0 2px 7px rgba(10,31,24,.1);}',

  '.pw-card__new-badge + .pw-card__brand-logo{top:41px;}',

  '.pw-card__actions{position:absolute;right:12px;bottom:12px;z-index:4;display:flex;align-items:center;gap:7px;}',

  '.pw-card__wishlist,.pw-card__cart{appearance:none;position:relative;display:flex;align-items:center;justify-content:center;width:40px;height:40px;padding:0;color:#26332d;background:#fff;border:1px solid #aab0ac;border-radius:50%;box-shadow:0 2px 7px rgba(0,0,0,.08);cursor:pointer;transition:transform .18s ease,border-color .18s ease,color .18s ease,background .18s ease;}',

  '.pw-card__wishlist:hover,.pw-card__cart:hover{color:#174f42;border-color:#174f42;transform:scale(1.08);}',

  '.pw-card__wishlist:focus-visible,.pw-card__cart:focus-visible{outline:2px solid #174f42;outline-offset:3px;}',

  '.pw-card__wishlist svg{width:20px;height:20px;fill:none;stroke:currentColor;stroke-width:1.6;stroke-linecap:round;stroke-linejoin:round;}',

  '.pw-card__wishlist.is-saved{color:#071f18;background:#c8ff3d;border-color:#c8ff3d;box-shadow:0 3px 10px rgba(7,31,24,.14);}',

  '.pw-card__wishlist.is-saved svg{fill:currentColor;stroke:currentColor;}',

  '.pw-card__cart svg{width:20px;height:20px;fill:none;stroke:currentColor;stroke-width:1.6;stroke-linecap:round;stroke-linejoin:round;}',

  '.pw-card__cart-check{display:none;}',

  '.pw-card__cart.is-added{color:#fff;background:#174f42;border-color:#174f42;}',

  '.pw-card__cart.is-added .pw-card__cart-icon{display:none;}',

  '.pw-card__cart.is-added .pw-card__cart-check{display:block;}',

  '.pw-card__cart.is-loading{color:transparent;pointer-events:none;}',

  '.pw-card__cart.is-loading:after{content:"";position:absolute;width:16px;height:16px;border:2px solid #c8d0cc;border-top-color:#174f42;border-radius:50%;animation:pwSpin .7s linear infinite;}',

  '.pw-card__cart:disabled{opacity:.45;cursor:not-allowed;transform:none;}',

  '.pw-card-cart-toast{position:fixed;right:24px;bottom:24px;z-index:9999;display:flex;align-items:center;gap:24px;max-width:calc(100vw - 32px);padding:16px 18px;color:#fff;background:#08271f;border-radius:2px;box-shadow:0 12px 35px rgba(0,0,0,.22);font-size:13px;font-weight:700;letter-spacing:.02em;}',

  '.pw-card-cart-toast[hidden]{display:none;}',

  '.pw-card-cart-toast.is-error{background:#8b1e1e;}',

  '.pw-card-cart-toast a{color:#c8ff3d;text-decoration:none;font-size:11px;font-weight:800;white-space:nowrap;}',
  /* PRODUCT IMAGE */

  '.pw-card__media{position:relative;width:100%;height:350px;background:#fff;overflow:hidden;display:flex;align-items:center;justify-content:center;box-sizing:border-box;}',

  '.pw-card__product-image{display:block;flex:0 0 auto;width:auto;max-width:none;height:312px;max-height:calc(100% - 20px);object-fit:contain;object-position:center center;background:#fff;padding:8px 0;box-sizing:border-box;transition:transform .25s ease;}',

  '.pw-card:hover .pw-card__product-image{transform:scale(1.04);}',

'.pw-card__brand-logo{position:absolute;top:10px;left:10px;z-index:4;display:block;width:auto;max-width:96px;height:auto;max-height:40px;object-fit:contain;object-position:left center;background:transparent;mix-blend-mode:multiply;padding:0;border:0;border-radius:0;box-shadow:none;pointer-events:none;}',

  '.pw-card__no-image{width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#fff;color:#999;font-size:12px;}',


  /* PRODUCT INFORMATION */

  '.pw-card__body{display:flex;flex-direction:column;flex:none;padding:11px 4px 10px;text-align:left;background:#fff;box-sizing:border-box;}',

  '.pw-card__vendor{text-transform:uppercase;font-size:12px;font-weight:800;line-height:1.2;letter-spacing:.045em;color:#14231e;margin:0 0 5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',

  '.pw-card__title{font-size:14px;line-height:1.32;min-height:20px;margin:0;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;color:#313a35;}',

  '.pw-card__meta{margin:5px 0 0;color:#6b726d;font-size:12px;line-height:1.3;}',

  '.pw-card__price{display:flex;align-items:baseline;justify-content:flex-start;gap:8px;margin-top:8px;padding-top:0;font-size:14px;line-height:1.2;}',

'.pw-card__original{color:#a40000;text-decoration:line-through;font-weight:400;}',

'.pw-card__current{font-size:18px;font-weight:800;color:#14231e;}',

'.pw-card__saving{margin:4px 0 0;color:#315d3b;font-size:11px;font-weight:600;line-height:1.2;}',

  '.pw-card__sold-context{margin:5px 0 0;color:#5f7f08;font-size:9px;font-weight:800;letter-spacing:.08em;line-height:1.2;}',


  /* LOADING / ERRORS */

  '.pw-search__loading{padding:12px 0 18px;color:#555;font-size:13px;}',

  '.pw-search__spinner{display:inline-block;width:14px;height:14px;border:2px solid #ddd;border-top-color:#111;border-radius:50%;animation:pwSpin .7s linear infinite;vertical-align:-2px;margin-right:5px;}',

  '@keyframes pwSpin{to{transform:rotate(360deg)}}',

  '.pw-search__error{padding:12px;margin-bottom:15px;border:1px solid #c33;background:#fff4f4;color:#8b0000;font-size:13px;}',

  '.pw-search__empty{grid-column:1/-1;padding:50px 10px;text-align:center;color:#666;}',


  /* FILTERS */

  '.pw-filter{border-bottom:1px solid #e5e5e5;}',

  '.pw-filter summary{list-style:none;cursor:pointer;display:flex;justify-content:space-between;align-items:center;padding:14px 0;font-size:13px;font-weight:700;color:#26332d;}',

  '.pw-filter summary::-webkit-details-marker{display:none;}',

  '.pw-filter__chevron{transition:transform .18s ease;}',

  '.pw-filter[open] .pw-filter__chevron{transform:rotate(180deg);}',

  '.pw-filter__body{padding:0 0 15px;}',

  '.pw-filter__sizes{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;}',

  '.pw-size{height:36px;border:1px solid #ccc;background:#fff;font-size:12px;cursor:pointer;overflow:hidden;text-overflow:ellipsis;padding:0 4px;}',

  '.pw-size:hover{border-color:#315d3b;}',

  '.pw-size.is-active{background:#315d3b;color:#fff;border-color:#315d3b;}',

  '.pw-size.is-disabled,.pw-colour.is-disabled,.pw-filter-value.is-disabled{opacity:.28;cursor:default;}',


  /* COLOUR */

  '.pw-filter__colours{display:flex;flex-wrap:wrap;gap:9px;}',

  '.pw-colour{width:31px;height:31px;border:1px solid transparent;background:none;border-radius:50%;padding:3px;cursor:pointer;}',

  '.pw-colour__dot{display:block;width:23px;height:23px;border-radius:50%;border:1px solid #aaa;box-sizing:border-box;}',

  '.pw-colour.is-active{border-color:#315d3b;}',


  /* STANDARD FILTER VALUES */

  '.pw-filter__list{display:flex;flex-direction:column;}',

  '.pw-filter-value{appearance:none;border:0;background:none;padding:6px 0;display:grid;grid-template-columns:17px 1fr auto;align-items:center;gap:8px;text-align:left;cursor:pointer;font:inherit;font-size:12px;}',

  '.pw-filter-value__box{width:15px;height:15px;border:1px solid #aaa;box-sizing:border-box;}',

  '.pw-filter-value.is-active .pw-filter-value__box{background:#315d3b;border-color:#315d3b;box-shadow:inset 0 0 0 3px #fff;}',

  '.pw-filter-value__count{color:#888;font-size:11px;}',

  '.pw-filter-value--extra{display:none;}',

  '.pw-filter__more{appearance:none;border:0;background:none;padding:8px 0 0;text-decoration:underline;cursor:pointer;font:inherit;font-size:12px;text-align:left;}',


  /* PRICE */

  '.pw-price{display:grid;grid-template-columns:1fr 1fr;gap:8px;}',

  '.pw-price label{font-size:11px;color:#666;}',

  '.pw-price input{display:block;width:100%;height:36px;margin-top:4px;border:1px solid #ccc;padding:0 7px;box-sizing:border-box;}',

  '.pw-price__apply{grid-column:1/-1;height:36px;border:1px solid #315d3b;background:#315d3b;color:#fff;cursor:pointer;}',


  /* PAGINATION */

  '.pw-search__pagination{display:flex;align-items:center;justify-content:center;padding:52px 0 14px;}',

  '.pw-page-shell{position:relative;width:min(100%,780px);padding:20px 24px 17px;border:1px solid #d8dfda;border-radius:18px;background:linear-gradient(135deg,#f7f9f5 0%,#fff 58%,#f2f7ef 100%);box-shadow:0 14px 34px rgba(12,43,33,.08);box-sizing:border-box;text-align:center;overflow:hidden;}',

  '.pw-page-shell:before{content:"";position:absolute;inset:0 0 auto;height:4px;background:linear-gradient(90deg,#baff2a 0 28%,#0a3025 28% 100%);}',

  '.pw-page-kicker{display:block;margin:0 0 13px;color:#496159;font-size:10px;font-weight:800;letter-spacing:.17em;text-transform:uppercase;}',

  '.pw-page-controls{display:flex;align-items:center;justify-content:center;gap:14px;}',

  '.pw-page-numbers{display:flex;align-items:center;justify-content:center;gap:6px;min-width:0;}',

  '.pw-page-button,.pw-page-number{appearance:none;display:inline-flex;align-items:center;justify-content:center;height:44px;border:1px solid #cbd5ce;background:#fff;color:#123128;font:inherit;font-size:12px;font-weight:750;line-height:1;cursor:pointer;box-sizing:border-box;transition:transform .16s ease,border-color .16s ease,background .16s ease,color .16s ease,box-shadow .16s ease;}',

  '.pw-page-button{gap:9px;min-width:118px;padding:0 18px;border-color:#0a3025;border-radius:999px;background:#0a3025;color:#fff;letter-spacing:.03em;}',

  '.pw-page-number{width:42px;padding:0;border-radius:50%;}',

  '.pw-page-button:not(:disabled):hover,.pw-page-number:not(.is-current):not(.is-disabled):hover{transform:translateY(-2px);border-color:#0a3025;box-shadow:0 7px 16px rgba(10,48,37,.13);}',

  '.pw-page-button:focus-visible,.pw-page-number:focus-visible{outline:3px solid rgba(186,255,42,.65);outline-offset:3px;}',

  '.pw-page-number.is-current{border-color:#baff2a;background:#baff2a;color:#08271f;box-shadow:0 6px 14px rgba(141,196,24,.25);}',

  '.pw-page-number.is-disabled{opacity:.32;cursor:default;}',

  '.pw-page-ellipsis{display:inline-flex;align-items:center;justify-content:center;width:24px;height:42px;color:#7b8983;font-size:10px;letter-spacing:.08em;}',

  '.pw-page-button:disabled{border-color:#d8ded9;background:#edf0ed;color:#9ca7a1;box-shadow:none;cursor:default;}',

  '.pw-page-status{display:block;margin-top:12px;color:#65746d;font-size:11px;letter-spacing:.02em;}',

  '.pw-page-status strong{color:#15332a;font-weight:800;}',

  '.pw-search__muted{color:#888;font-size:12px;}',

  '.pw-visually-hidden{position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;white-space:nowrap!important;border:0!important;}',

  '#PrewornWideSearch.is-loading .pw-search__grid{opacity:.45;pointer-events:none;}',


  /* LARGE / MEDIUM DESKTOP */

  '@media(max-width:1699px){' +
    '.pw-search__grid{grid-template-columns:repeat(4,minmax(0,1fr));}' +
    '.pw-card__media{height:350px;}' +
  '}',


  /* TABLET / SMALL DESKTOP */

  '@media(max-width:1199px){' +
    '.pw-search{grid-template-columns:230px minmax(0,1fr);gap:22px;padding-left:18px;padding-right:18px;}' +
    '.pw-search__grid{grid-template-columns:repeat(3,minmax(0,1fr));}' +
    '.pw-filter__sizes{grid-template-columns:repeat(3,minmax(0,1fr));}' +
    '.pw-card__media{height:315px;}' +
    '.pw-card__product-image{height:280px;}' +
  '}',


  /* MOBILE */

  '@media(max-width:749px){' +
    '.pw-search{display:block;padding:0 14px 35px;}' +
    '.pw-search__results{padding-top:8px;}' +
    'body.pw-search-filter-open{overflow:hidden;}' +
    '.pw-search__filters{display:block;position:fixed;inset:0;z-index:10010;width:100%;max-width:none;max-height:100vh;max-height:100dvh;overflow-y:auto;overscroll-behavior:contain;border-right:0;padding:0 18px 28px;margin:0;background:#fff;box-sizing:border-box;visibility:hidden;pointer-events:none;transform:translateX(-100%);transition:transform .24s ease,visibility 0s linear .24s;}' +
    '.pw-search__filters.is-mobile-open{visibility:visible;pointer-events:auto;transform:translateX(0);transition:transform .24s ease;}' +
    '.pw-search__filter-head{position:sticky;top:0;z-index:2;min-height:64px;padding:10px 0;background:#fff;}' +
    '.pw-search__filter-close{display:inline-flex;}' +
    '.pw-search__intro{margin-bottom:13px;padding-bottom:14px;}' +
    '.pw-search__query{font-size:23px;}' +
    '.pw-quick-filters{flex-wrap:nowrap;overflow-x:auto;margin-left:-14px;margin-right:-14px;padding:0 14px 4px;scrollbar-width:none;}' +
    '.pw-quick-filters::-webkit-scrollbar{display:none;}' +
    '.pw-search__toolbar{gap:10px;}' +
    '.pw-search__filter-toggle{display:inline-flex;align-items:center;gap:5px;}' +
    '.pw-search__sort{margin-left:auto;}' +
    '.pw-search__sort span{display:none;}' +
    '.pw-search__sort select{min-width:138px;max-width:44vw;}' +
    '.pw-search__grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:16px 10px;}' +
    '.pw-filter__sizes{grid-template-columns:repeat(5,minmax(0,1fr));}' +
    '.pw-card__media{height:230px;}' +
    '.pw-card__product-image{height:208px;max-height:calc(100% - 12px);}' +
    '.pw-card__brand-logo{top:7px;left:7px;max-width:78px;max-height:34px;padding:0;}' +
    '.pw-card__body{padding:9px 2px 8px;}' +
    '.pw-card__title{font-size:12px;min-height:20px;}' +
    '.pw-card__vendor{font-size:10px;}' +
    '.pw-card__meta{font-size:11px;}' +
    '.pw-card__price{font-size:13px;}' +
    '.pw-card__current{font-size:16px;}' +
    '.pw-card__discount{width:46px;height:46px;right:7px;top:7px;}' +
    '.pw-card__discount span{font-size:14px;}' +
    '.pw-card__actions{right:8px;bottom:8px;gap:5px;}' +
    '.pw-card__wishlist,.pw-card__cart{width:35px;height:35px;}' +
    '.pw-card__wishlist svg,.pw-card__cart svg{width:18px;height:18px;}' +
    '.pw-card-cart-toast{right:12px;bottom:12px;left:12px;justify-content:space-between;}' +
    '.pw-search__pagination{padding:35px 0 4px;}' +
    '.pw-page-shell{padding:17px 12px 14px;border-radius:14px;}' +
    '.pw-page-kicker{margin-bottom:11px;}' +
    '.pw-page-controls{gap:7px;}' +
    '.pw-page-numbers{gap:5px;}' +
    '.pw-page-button{min-width:42px;width:42px;height:42px;padding:0;}' +
    '.pw-page-button__label{display:none;}' +
    '.pw-page-number{width:40px;height:40px;}' +
    '.pw-page-number:not(.is-current):not(.pw-page-number--near),.pw-page-ellipsis{display:none;}' +
    '.pw-page-status{margin-top:10px;}' +
  '}'

].join('');

    document.head.appendChild(style);
  }

  /* The homepage uses this exact renderer so product cards, pricing,
     wishlist behaviour and styles cannot drift from browse/search cards. */
  window.PrewornProductCards = {
    brandLogoFilename: function (vendor) {
      var names = brandLogoAssetNames(vendor);
      return names.length ? names[0] : '';
    },
    renderList: function (products) {
      injectStyles();
      var items = Array.isArray(products) ? products : [];
      var wishlistIndex = getWishlistIndex();
      var html = '';

      for (var i = 0; i < items.length; i++) {
        html += productCard(items[i], wishlistIndex);
      }

      return html;
    },
    bindWishlist: function (scope) {
      if (scope) bindWishlistButtons(scope);
    },
    ensureStyles: injectStyles
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
